
// ...existing code (الاستيرادات فقط في الأعلى)...
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './SketchPage.css';
import { getLanguageTranslations } from '../../utilities/translations';

// مقياس الرسم: كل بيكسل = 10 مم
const MM_PER_PIXEL = 10;

const createDefaultLayers = () => {
  const tEn = getLanguageTranslations('en');
  return {
    layer1: { name: tEn.baseLayer, visible: true, color: '#3498db' },
    layer2: { name: tEn.linesLayer, visible: true, color: '#e74c3c' },
    layer3: { name: tEn.textLayer, visible: true, color: '#FF9800' },
    measurements: { name: tEn.measurementsLayer, visible: true, color: '#9b59b6' }
  };
};

const createEmptyPage = (id, label, kind = 'custom') => ({
  id,
  label,
  kind,
  elements: [],
  layers: createDefaultLayers(),
  history: [[]],
  historyIndex: 0,
  zoom: 1,
  pan: { x: 0, y: 0 }
});

const ensureMeasurementsLayer = (layers = createDefaultLayers()) => {
  if (layers.measurements) return layers;
  const tEn = getLanguageTranslations('en');
  return {
    ...layers,
    measurements: { name: tEn.measurementsLayer, visible: true, color: '#9b59b6' }
  };
};

const hydratePage = (page, fallbackId, fallbackLabel, kind = 'custom') => {
  const id = page?.id || fallbackId;
  const base = createEmptyPage(id, fallbackLabel, kind);
  return {
    ...base,
    ...page,
    id,
    label: page?.label || fallbackLabel,
    kind: page?.kind || kind,
    elements: page?.elements ? [...page.elements] : base.elements,
    layers: ensureMeasurementsLayer(page?.layers || base.layers),
    history: Array.isArray(page?.history) && page.history.length ? page.history : base.history,
    historyIndex: typeof page?.historyIndex === 'number' ? page.historyIndex : base.historyIndex,
    zoom: typeof page?.zoom === 'number' ? page.zoom : base.zoom,
    pan: page?.pan ? { ...page.pan } : base.pan
  };
};

const defaultPageLabel = (id) => {
  const lang = localStorage.getItem('app_language') || 'ar';
  const t = getLanguageTranslations(lang);
  if (id === 'floor') return t.floor;
  if (id === 'ceiling') return t.ceiling;
  if (id?.startsWith('wall')) return t.wall;
  return id || t.page;
};

const inferKind = (id) => {
  if (id === 'floor') return 'floor';
  if (id === 'ceiling') return 'ceiling';
  if (id?.startsWith('wall')) return 'wall';
  return 'custom';
};

const SketchPage = () => {
    // تحقق من وجود مشروع نشط
    const [hasActiveProject, setHasActiveProject] = useState(() => {
      return !!localStorage.getItem('cad_last_project_id');
    });

    // راقب تغييرات المشروع النشط
    useEffect(() => {
      const checkActiveProject = () => {
        setHasActiveProject(!!localStorage.getItem('cad_last_project_id'));
      };
      window.addEventListener('storage', checkActiveProject);
      const interval = setInterval(checkActiveProject, 1000);
      return () => {
        window.removeEventListener('storage', checkActiveProject);
        clearInterval(interval);
      };
    }, []);
  const svgRef = useRef(null);
  
  // نظام اللغة
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('app_language') || 'ar';
  });
  const t = getLanguageTranslations(language);
  const tEn = getLanguageTranslations('en');
  const tAr = getLanguageTranslations('ar');
  // دالة تعرض الاسم حسب اللغة المختارة
  // Returns label in current language only
  const singleLabel = (key) => {
    if (language === 'ar') return tAr[key] || key;
    return tEn[key] || key;
  };
  // تحديث أسماء الطبقات وعناوين الصفحات عند تغيير اللغة
  useEffect(() => {
    setPagesById(prev => {
      if (!prev) return prev;
      const updated = {};
      for (const [id, page] of Object.entries(prev)) {
        const newLayers = { ...page.layers };
        if (newLayers.layer1) newLayers.layer1 = { ...newLayers.layer1, name: t.baseLayer };
        if (newLayers.layer2) newLayers.layer2 = { ...newLayers.layer2, name: t.linesLayer };
        if (newLayers.layer3) newLayers.layer3 = { ...newLayers.layer3, name: t.textLayer };
        if (newLayers.measurements) newLayers.measurements = { ...newLayers.measurements, name: t.measurementsLayer };
        updated[id] = { ...page, label: defaultPageLabel(id), layers: newLayers };
      }
      return updated;
    });
  }, [language]);

  // استمع لتغييرات اللغة من localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const newLang = localStorage.getItem('app_language') || 'ar';
      setLanguage(newLang);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // تحقق من التغييرات كل 500 ميلي ثانية (للتغييرات في نفس التبويب)
    const interval = setInterval(() => {
      const currentLang = localStorage.getItem('app_language') || 'ar';
      if (currentLang !== language) {
        setLanguage(currentLang);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [language]);
  
  const [selectedTool, setSelectedTool] = useState('line');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentElement, setCurrentElement] = useState(null);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [elements, setElements] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // ميزات متقدمة
  const [magnetEnabled, setMagnetEnabled] = useState(true);
  // زيادة عتبة الالتقاط ليتم السحب من مسافة أبعد
  const [snapThreshold] = useState(20);
  const [snapPoint, setSnapPoint] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(5);
  const FIXED_LAYERS = [
    { key: 'layer1', nameKey: 'baseLayer', color: '#3498db' },
    { key: 'layer2', nameKey: 'linesLayer', color: '#e74c3c' },
    { key: 'layer3', nameKey: 'textLayer', color: '#FF9800' },
    { key: 'measurements', nameKey: 'measurementsLayer', color: '#9b59b6' }
  ];
  const [layers, setLayers] = useState(() => {
    const t = getLanguageTranslations(language);
    const obj = {};
    for (const l of FIXED_LAYERS) {
      obj[l.key] = { name: t[l.nameKey], visible: true, color: l.color };
    }
    return obj;
  });
  
  // نظام تحديد وتعديل العناصر
  const [selectedElement, setSelectedElement] = useState(null);
  const [showProperties, setShowProperties] = useState(false);
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showBoundaryWarning, setShowBoundaryWarning] = useState(false);
  
  // نظام تحريك جميع العناصر
  const [isDraggingAll, setIsDraggingAll] = useState(false);
  const [allElementsStartPositions, setAllElementsStartPositions] = useState([]);
  
  // نظام الرباعي
  const [showQuadrilateralModal, setShowQuadrilateralModal] = useState(false);
  const [, setHasQuadrilateral] = useState(false);
  const [quadrilateralData, setQuadrilateralData] = useState({
    sideA: '',
    sideB: '',
    sideC: '',
    sideD: '',
    diagonal1: '',
    diagonal2: '',
    startX: 0,
    startY: 0
  });
  
  // نظام إدخال قياس الزاوية
  const [showAngleModal, setShowAngleModal] = useState(false);
  const [angleValue, setAngleValue] = useState('90');
  const [pendingAngleId, setPendingAngleId] = useState(null);
  // نظام إدخال الزاوية المخصصة
  const [showCustomAngleModal, setShowCustomAngleModal] = useState(false);
  const [customAngleData, setCustomAngleData] = useState({
    angle: '90',
    length1: '1000',
    length2: '1000',
    lineWidth1: '100',
    lineWidth2: '100',
    name1: 'AB',
    name2: 'AC',
    startX: 0,
    startY: 0
  });
  const [showFloorDoorDirectionModal, setShowFloorDoorDirectionModal] = useState(false);
  // إضافة 8 نماذج: 4 عادية و4 معكوسة (تبديل الألوان)
  const [selectedFloorDoorDirection, setSelectedFloorDoorDirection] = useState('bottom-left');
  // الاتجاهات الأصلية
  const FLOOR_DOOR_DIRECTIONS = [
    { key: 'bottom-left', label: 'أسفل-يسار', icon: '↙️' },
    { key: 'bottom-right', label: 'أسفل-يمين', icon: '↘️' },
    { key: 'top-left', label: 'أعلى-يسار', icon: '↖️' },
    { key: 'top-right', label: 'أعلى-يمين', icon: '↗️' },
  ];
  // الاتجاهات المعكوسة (تبديل الألوان)
  const FLOOR_DOOR_DIRECTIONS_FLIPPED = [
    { key: 'bottom-left-flip', label: 'أسفل-يسار (معكوس)', icon: '↙️' },
    { key: 'bottom-right-flip', label: 'أسفل-يمين (معكوس)', icon: '↘️' },
    { key: 'top-left-flip', label: 'أعلى-يسار (معكوس)', icon: '↖️' },
    { key: 'top-right-flip', label: 'أعلى-يمين (معكوس)', icon: '↗️' },
  ];

  // دالة اختيار اتجاه الباب الأرضي
  const selectFloorDoorDirection = useCallback((direction) => {
    setSelectedFloorDoorDirection(direction);
    setSelectedTool('component');
    setSelectedComponentLabel('floorDoor');
    setShowFloorDoorDirectionModal(false);
  }, []);
  // نظام إدخال النص
  const [showTextModal, setShowTextModal] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [textSizeValue, setTextSizeValue] = useState('16');
  const [pendingTextId, setPendingTextId] = useState(null);
  // تتبع العنصر الجاري رسمه (حل مشكلة اختفاء أول عنصر)
  const [drawingElementId, setDrawingElementId] = useState(null);
  
  // نظام اختيار اتجاه الزاوية المسبق
  const [selectedAngleDirection, setSelectedAngleDirection] = useState('horizontal_right_up');
  
  // مكونات مستطيلة مملوءة
  const [selectedComponentLabel, setSelectedComponentLabel] = useState(null);
  // ألوان مختلفة للمكونات حسب النوع
  const getComponentColor = (componentType) => {
    const colors = {
      'wallDoor': '#8b4513',    // بني للباب الجداري
      'floorDoor': '#ffffff',   // أبيض للباب الأرضي
      'window': '#87ceeb',      // أزرق فاتح للنافذة
      'airConditioner': '#2c3e50', // رمادي غامق للمكيف
    };
    return colors[componentType] || '#3498db'; // لون افتراضي
  };
  
  // إعدادات عامة للواجهة
  const [showToolbar, setShowToolbar] = useState(true);
  
  // نظام الصفحات داخل المشروع
  const [pagesById, setPagesById] = useState(null); // {id: Page}
  const [pageOrder, setPageOrder] = useState([]); // [id]
  const [activePageId, setActivePageId] = useState('floor');
  const [collapsed, setCollapsed] = useState({
    tools: true,
    projects: true,
    elements: true,
    layers: true,
    angle: true,
    coords: true,
    settings: true,
    basic: true
  });
  
  // نظام الحائط الأساسي
  const [baseWall, setBaseWall] = useState(null); // الحائط الأساسي (مستطيل)
  
  // نظام إدارة المشروع والأجهزة
  const [showDevicesPage, setShowDevicesPage] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceNote, setDeviceNote] = useState('');
  const [deviceForm, setDeviceForm] = useState({
    id: '',
    name: '',
    model: '',
    w: '',
    h: '',
    d: '',
    notes: ''
  });
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  
  // معلومات المشروع
  const [projectInfo, setProjectInfo] = useState({
    projectNumber: '',
    clientName: '',
    address: '',
    phone: '',
    measuredBy: '',
    branch: '',
    version: '1.0.0',
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  });

  // دالة تحديث الإصدار تلقائياً
  const incrementVersion = useCallback(() => {
    setProjectInfo(prev => {
      const versionParts = prev.version.split('.').map(Number);
      versionParts[2] = (versionParts[2] || 0) + 1; // زيادة الإصدار الفرعي
      const newVersion = versionParts.join('.');
      
      const updatedProjectInfo = {
        ...prev,
        version: newVersion,
        lastModified: new Date().toISOString()
      };

      // حفظ التحديث في localStorage فوراً
      const lastProjectId = localStorage.getItem('cad_last_project_id');
      if (lastProjectId) {
        try {
          // تحديث بيانات المشروع
          const projectData = localStorage.getItem(`cad_project:${lastProjectId}`);
          if (projectData) {
            const parsedData = JSON.parse(projectData);
            parsedData.projectInfo = updatedProjectInfo;
            localStorage.setItem(`cad_project:${lastProjectId}`, JSON.stringify(parsedData));
          }

          // تحديث قائمة المشاريع
          const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
          const updatedList = projectsList.map(p => 
            p.id === lastProjectId 
              ? { ...p, version: newVersion, lastModified: updatedProjectInfo.lastModified }
              : p
          );
          localStorage.setItem('cad_projects_list', JSON.stringify(updatedList));
        } catch (error) {
          console.error('خطأ في حفظ الإصدار المحدث:', error);
        }
      }

      return updatedProjectInfo;
    });
  }, []);

  // تحديث الإصدار عند أي تغيير في المشروع
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // عدادات التراتبية للأدوات الأساسية
  const [componentCounters, setComponentCounters] = useState({
    'E': 0,    // كهرباء
    'W': 0,    // ماء
    'G': 0,    // غاز
    'H': 0,    // شطاف
    'DN': 0,   // مجرى
    'A': 0,    // صيانة
    'C': 0,    // مكيف
    'R': 0,    // مجرى جداري
    'FD': 0    // باب أرضي
  });

  // دالة لحساب عدادات العناصر من العناصر الموجودة
  const calculateComponentCounters = useCallback((elements) => {
    // خريطة الأدوات والرموز الجديدة
    const componentSymbolMap = {
      'كهرباء': 'E',
      'ماء': 'W',
      'غاز': 'G',
      'شطاف': 'H',
      'مجرى': 'DN',
      'صيانة': 'A',
      'مكيف': 'C',
      'مجرى جداري': 'R'
    };
    const counters = {
      'E': 0, 'W': 0, 'G': 0, 'H': 0, 'DN': 0, 'A': 0, 'C': 0, 'R': 0, 'FD': 0
    };
    
    elements.forEach(element => {
      if (element.type === 'component' && element.label) {
        // البحث عن العناصر التي تبدأ برموز المكونات
        Object.keys(counters).forEach(symbol => {
          if (element.label.startsWith(symbol)) {
            const numberMatch = element.label.match(new RegExp(`^${symbol}(\\d+)$`));
            if (numberMatch) {
              const number = parseInt(numberMatch[1], 10);
              if (number > counters[symbol]) {
                counters[symbol] = number;
              }
            }
          }
        });
      }
    });
    
    return counters;
  }, []);

  // دالة لإنشاء رمز مع الرقم التسلسلي
  const generateComponentSymbol = useCallback((componentType) => {
    // خريطة الأدوات والرموز الجديدة
    const componentSymbolMap = {
      electricity: 'E',
      water: 'W',
      gas: 'G',
      bidetSprayer: 'H',
      duct: 'DN',
      maintenance: 'A',
      airConditioner: 'C',
      wallDuct: 'R',
      wallDoor: 'E',
      wallWindow: 'W',
      floorDoor: 'FD'
    };
    
    const symbol = componentSymbolMap[componentType];
    if (!symbol) return componentType;
    
    const newCounter = componentCounters[symbol] + 1;
    setComponentCounters(prev => ({
      ...prev,
      [symbol]: newCounter
    }));
    
    return `${symbol}${newCounter}`;
  }, [componentCounters]);

  // تحديث العدادات عند تغيير العناصر
  useEffect(() => {
    const newCounters = calculateComponentCounters(elements);
    setComponentCounters(newCounters);
  }, [elements, calculateComponentCounters]);

  // مراقبة التغييرات لتحديث الإصدار
  useEffect(() => {
    if (hasUnsavedChanges) {
      const timer = setTimeout(() => {
        incrementVersion();
        setHasUnsavedChanges(false);
      }, 2000); // تحديث الإصدار بعد ثانيتين من التوقف عن التعديل
      
      return () => clearTimeout(timer);
    }
  }, [hasUnsavedChanges, incrementVersion]);
  
  // نظام إدخال قيم الأدوات الأساسية
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [componentInputData, setComponentInputData] = useState({
    width: '',
    height: '',
    distanceFromBottom: '',
    distanceFromLeft: ''
  });

  const toggleGroup = useCallback((key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    if (selectedTool === 'angle') {
      setCollapsed(prev => prev.angle ? { ...prev, angle: false } : prev);
    }
  }, [selectedTool]);

  // حفظ حالة الصفحة عند تغيير العناصر أو الطبقات - سيتم تعريفه بعد persistActivePageState

  // سحب لتحريك العنصر من لوحة الخصائص
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const elementDragSnapshotRef = useRef(null);
  
  // ألوان الأضلاع
  const sideColors = {
    AB: '#e74c3c', // أحمر
    BC: '#2196F3', // أزرق  
    CD: '#4CAF50', // أخضر
    DA: '#FF9800'  // برتقالي
  };

  // دالة مساعدة لحساب أقرب نقطة على خط
  const closestPointOnLine = (point, lineStart, lineEnd) => {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lengthSquared = dx * dx + dy * dy;
    
    if (lengthSquared === 0) return lineStart;
    
    let t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));
    
    return {
      x: lineStart.x + t * dx,
      y: lineStart.y + t * dy
    };
  };

  // البحث عن أقرب نقطة للالتصاق (Magnet Feature) - محسّن
  const findNearbyPoint = useCallback((point, elements, currentElementId) => {
    if (!magnetEnabled) return null;

    let nearestPoint = null;
    let minDistance = snapThreshold;

    elements.forEach(element => {
      if (element.id === currentElementId || !element.visible) return;

      const points = [];
      const lines = [];
      
      // استخراج جميع النقاط والخطوط من العنصر
      if (element.type === 'line') {
        // نقاط الزوايا
        points.push({ x: element.x1, y: element.y1 });
        points.push({ x: element.x2, y: element.y2 });
        // الخط نفسه
        lines.push({ start: { x: element.x1, y: element.y1 }, end: { x: element.x2, y: element.y2 } });
        
      } else if (element.type === 'rectangle' || element.type === 'baseWall' || element.type === 'component') {
        const x = element.x;
        const y = element.y;
        const width = element.width || 0;
        const height = element.height || 0;
        
        // نقاط الزوايا الأربعة
        points.push({ x, y });
        points.push({ x: x + width, y });
        points.push({ x: x + width, y: y + height });
        points.push({ x, y: y + height });
        
        // الأضلاع الأربعة
        lines.push({ start: { x, y }, end: { x: x + width, y } }); // أعلى
        lines.push({ start: { x: x + width, y }, end: { x: x + width, y: y + height } }); // يمين
        lines.push({ start: { x: x + width, y: y + height }, end: { x, y: y + height } }); // أسفل
        lines.push({ start: { x, y: y + height }, end: { x, y } }); // يسار
        
      } else if (element.type === 'circle') {
        // مركز الدائرة
        points.push({ x: element.cx, y: element.cy });
        // نقاط على محيط الدائرة (8 نقاط)
        const r = element.r || 0;
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI) / 4;
          points.push({
            x: element.cx + r * Math.cos(angle),
            y: element.cy + r * Math.sin(angle)
          });
        }
        
      } else if (element.type === 'dimension') {
        points.push({ x: element.x1, y: element.y1 });
        points.push({ x: element.x2, y: element.y2 });
        lines.push({ start: { x: element.x1, y: element.y1 }, end: { x: element.x2, y: element.y2 } });
        
      } else if (element.type === 'angle' || element.type === 'customAngle') {
        // نقطة المركز
        points.push({ x: element.centerX, y: element.centerY });
        
        // نقاط نهايات الخطوط
        if (element.type === 'customAngle') {
          const angle1Rad = 0;
          const angle2Rad = (element.angle * Math.PI) / 180;
          
          const endPoint1 = {
            x: element.centerX + element.length1 * Math.cos(angle1Rad),
            y: element.centerY + element.length1 * Math.sin(angle1Rad)
          };
          const endPoint2 = {
            x: element.centerX + element.length2 * Math.cos(angle2Rad),
            y: element.centerY + element.length2 * Math.sin(angle2Rad)
          };
          
          points.push(endPoint1);
          points.push(endPoint2);
          lines.push({ start: { x: element.centerX, y: element.centerY }, end: endPoint1 });
          lines.push({ start: { x: element.centerX, y: element.centerY }, end: endPoint2 });
        }
        
      } else if (element.type === 'quadrilateral') {
        // نقاط الرباعي
        if (element.vertices) {
          element.vertices.forEach(vertex => {
            points.push({ x: vertex.x, y: vertex.y });
          });
          // الأضلاع
          for (let i = 0; i < element.vertices.length; i++) {
            const next = (i + 1) % element.vertices.length;
            lines.push({
              start: { x: element.vertices[i].x, y: element.vertices[i].y },
              end: { x: element.vertices[next].x, y: element.vertices[next].y }
            });
          }
        }
      }

      // التحقق من المسافة إلى كل نقطة زاوية
      points.forEach(p => {
        const dx = p.x - point.x;
        const dy = p.y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestPoint = { x: p.x, y: p.y };
        }
      });

      // التحقق من المسافة إلى أقرب نقطة على كل خط/ضلع
      lines.forEach(line => {
        const closestPt = closestPointOnLine(point, line.start, line.end);
        const dx = closestPt.x - point.x;
        const dy = closestPt.y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestPoint = { x: closestPt.x, y: closestPt.y };
        }
      });
    });

    return nearestPoint;
  }, [magnetEnabled, snapThreshold]);

  // تحويل إحداثيات الماوس إلى إحداثيات SVG مع مراعاة التكبير والتحريك
  const getSVGPoint = useCallback((event) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    // بعد إضافة التحريك البصري بالترجمة، الإحداثيات لا تحتاج طرح pan
    let x = (event.clientX - rect.left) / zoom;
    let y = (event.clientY - rect.top) / zoom;

    if (snapToGrid) {
      x = Math.round(x / gridSize) * gridSize;
      y = Math.round(y / gridSize) * gridSize;
    } else {
      x = Math.round(x);
      y = Math.round(y);
    }

    return { x, y };
  }, [zoom, snapToGrid, gridSize]);

  // حفظ الحالة في التاريخ
  const saveToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...elements]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [elements, history, historyIndex]);

  // التراجع
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      const previousElements = history[historyIndex - 1];
      setElements(previousElements);
      // تحديث baseWall
      const wall = previousElements.find(el => el.type === 'baseWall');
      setBaseWall(wall || null);
    }
  }, [history, historyIndex]);

  // الإعادة
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      const nextElements = history[historyIndex + 1];
      setElements(nextElements);
      // تحديث baseWall
      const wall = nextElements.find(el => el.type === 'baseWall');
      setBaseWall(wall || null);
    }
  }, [history, historyIndex]);

  // تحميل صفحة إلى حالة الواجهة
  const loadPageToState = useCallback((pageObj) => {
    const p = hydratePage(pageObj || {}, pageObj?.id || activePageId, defaultPageLabel(pageObj?.id || activePageId), inferKind(pageObj?.id || activePageId));
    setElements([...p.elements]);
    setLayers(p.layers);
    setHistory(p.history);
    setHistoryIndex(p.historyIndex);
    setZoom(p.zoom);
    setPan(p.pan);
    setSelectedElement(null);
    
    // تحديث عدادات المكونات بناءً على العناصر المحملة
    const newCounters = calculateComponentCounters(p.elements);
    setComponentCounters(newCounters);
    
    // تحديث baseWall
    const wall = p.elements.find(el => el.type === 'baseWall');
    setBaseWall(wall || null);
  }, [activePageId, calculateComponentCounters]);

  // حفظ حالة الصفحة النشطة داخل نموذج الصفحات
  const persistActivePageState = useCallback(() => {
    if (!activePageId || !pagesById) return;
    
    setPagesById(prev => {
      if (!prev) return prev;
      
      const existing = prev[activePageId] || createEmptyPage(activePageId, defaultPageLabel(activePageId), inferKind(activePageId));
      const updated = {
        ...existing,
        elements: [...elements],
        layers: { ...layers },
        history: [...history],
        historyIndex,
        zoom,
        pan: { ...pan }
      };
      const newPagesById = { ...prev, [activePageId]: updated };
      
      // حفظ البيانات في localStorage مع معلومات إضافية
      const lastProjectId = localStorage.getItem('cad_last_project_id');
      if (lastProjectId) {
        const projectData = {
          pagesById: newPagesById,
          pageOrder,
          activePageId,
          snapToGrid,
          gridSize,
          selectedAngleDirection,
          projectInfo,
          devices,
          deviceNote,
          lastModified: new Date().toISOString()
        };
        localStorage.setItem(`cad_project:${lastProjectId}`, JSON.stringify(projectData));
      }
      
      return newPagesById;
    });
  }, [activePageId, pagesById, elements, layers, history, historyIndex, zoom, pan, pageOrder, snapToGrid, gridSize, selectedAngleDirection, projectInfo, devices, deviceNote]);

  // حفظ حالة الصفحة عند تغيير العناصر أو الطبقات (مع تأخير لتجنب التحديث المستمر)
  useEffect(() => {
    if (pagesById && activePageId) {
      const timeoutId = setTimeout(() => {
        persistActivePageState();
        setHasUnsavedChanges(true); // تحديد أن هناك تغييرات غير محفوظة
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [elements, layers, history, historyIndex, zoom, pan, activePageId, pagesById, persistActivePageState]);

  // تطبيق بيانات مشروع (نموذج صفحات أو نموذج قديم)
  const applyProjectData = useCallback((data) => {
    try {
      // نموذج الصفحات الجديد
      if (data && data.pagesById) {
        const incomingEntries = Object.entries(data.pagesById);
        const normalized = {};
        const ids = [];
        incomingEntries.forEach(([id, page]) => {
          const label = page?.label || defaultPageLabel(id);
          const kind = page?.kind || inferKind(id);
          normalized[id] = hydratePage(page, id, label, kind);
          ids.push(id);
        });
        const order = Array.isArray(data.pageOrder) && data.pageOrder.length ? data.pageOrder : ids;
        const activeId = data.activePageId && normalized[data.activePageId] ? data.activePageId : order[0];
        setPagesById(normalized);
        setPageOrder(order);
        setActivePageId(activeId);
        if (typeof data.snapToGrid === 'boolean') setSnapToGrid(data.snapToGrid);
        if (data.gridSize) setGridSize(data.gridSize);
        if (data.selectedAngleDirection) setSelectedAngleDirection(data.selectedAngleDirection);
        
        // تحديث معلومات المشروع والأجهزة
        if (data.projectInfo) {
          setProjectInfo({
            ...data.projectInfo,
            version: data.projectInfo.version || '1.0.0',
            createdAt: data.projectInfo.createdAt || new Date().toISOString(),
            lastModified: data.projectInfo.lastModified || new Date().toISOString()
          });
        }
        if (data.devices) setDevices(data.devices);
        if (data.deviceNote) setDeviceNote(data.deviceNote);
        
        loadPageToState(normalized[activeId]);
        return;
      }

      // نموذج قديم (صفحة واحدة)
      const floor = hydratePage({
        elements: data?.elements || [],
        layers: data?.layers || createDefaultLayers(),
        history: [[]],
        historyIndex: 0,
        zoom: data?.zoom || 1,
        pan: data?.pan || { x: 0, y: 0 }
      }, 'floor', 'أرضية', 'floor');
      const ceiling = createEmptyPage('ceiling', 'سقف', 'ceiling');
      setPagesById({ floor, ceiling });
      setPageOrder(['floor', 'ceiling']);
      setActivePageId('floor');
      if (typeof data?.snapToGrid === 'boolean') setSnapToGrid(data.snapToGrid);
      if (data?.gridSize) setGridSize(data.gridSize);
      if (data?.selectedAngleDirection) setSelectedAngleDirection(data.selectedAngleDirection);
      
      // تحديث معلومات المشروع والأجهزة للنموذج القديم
      if (data?.projectInfo) {
        setProjectInfo({
          ...data.projectInfo,
          version: data.projectInfo.version || '1.0.0',
          createdAt: data.projectInfo.createdAt || new Date().toISOString(),
          lastModified: data.projectInfo.lastModified || new Date().toISOString()
        });
      }
      if (data?.devices) setDevices(data.devices);
      if (data?.deviceNote) setDeviceNote(data.deviceNote);
      
      loadPageToState(floor);
    } catch (e) {
      console.error('فشل تطبيق بيانات المشروع:', e);
    }
  }, [loadPageToState]);

  // نظام المشاريع المحلي تمت إزالته

  // تهيئة افتراضية للصفحات في حال عدم وجودها
  useEffect(() => {
    if (!pagesById) {
      // التحقق من وجود مشروع نشط أولاً
      const lastProjectId = localStorage.getItem('cad_last_project_id');
      if (lastProjectId) {
        const projectData = localStorage.getItem(`cad_project:${lastProjectId}`);
        if (projectData) {
          try {
            const parsedData = JSON.parse(projectData);
            console.log('Loading active project:', lastProjectId, parsedData);
            applyProjectData(parsedData);
            return;
          } catch (error) {
            console.error('خطأ في تحميل المشروع النشط:', error);
          }
        }
      }
      
      // إذا لم يكن هناك مشروع نشط، إنشاء صفحات افتراضية
      console.log('Initializing default pages (no active project)');
      const floorPage = createEmptyPage('floor', t.floor || 'أرضية', 'floor');
      const ceilingPage = createEmptyPage('ceiling', 'سقف', 'ceiling');
      setPagesById({ floor: floorPage, ceiling: ceilingPage });
      setPageOrder(['floor', 'ceiling']);
      setActivePageId('floor');
      loadPageToState(floorPage);
    }
  }, [pagesById, loadPageToState, applyProjectData]);

  // التعامل مع سحب العنصر من لوحة الخصائص
  const startDragSelectedFromPanel = useCallback((event) => {
    if (!selectedElement) return;
    event.preventDefault();
    dragStartRef.current = { x: event.clientX, y: event.clientY };
    // حفظ لقطة للبيانات الأصلية للعنصر
    let data = null;
    switch (selectedElement.type) {
      case 'line':
        data = { x1: selectedElement.x1, y1: selectedElement.y1, x2: selectedElement.x2, y2: selectedElement.y2 };
        break;
      case 'rectangle':
        data = { x: selectedElement.x, y: selectedElement.y };
        break;
      case 'circle':
        data = { cx: selectedElement.cx, cy: selectedElement.cy };
        break;
      case 'dimension':
        data = { x1: selectedElement.x1, y1: selectedElement.y1, x2: selectedElement.x2, y2: selectedElement.y2 };
        break;
      case 'angle':
        data = { centerX: selectedElement.centerX, centerY: selectedElement.centerY };
        break;
      case 'customAngle':
        data = { centerX: selectedElement.centerX, centerY: selectedElement.centerY };
        break;
      case 'text':
        data = { x: selectedElement.x, y: selectedElement.y };
        break;
      case 'quadrilateral':
        data = { points: selectedElement.points.map(p => ({ x: p.x, y: p.y })) };
        break;
      case 'component':
        data = { x: selectedElement.x, y: selectedElement.y };
        break;
      case 'baseWall':
        data = { x: selectedElement.x, y: selectedElement.y };
        break;
      default:
        data = {};
    }
    elementDragSnapshotRef.current = { id: selectedElement.id, type: selectedElement.type, data };
    setIsDraggingSelection(true);
  }, [selectedElement]);

  useEffect(() => {
    if (!isDraggingSelection) return;
    const handleMove = (event) => {
      const snap = elementDragSnapshotRef.current;
      if (!snap) return;
      const dxScreen = event.clientX - dragStartRef.current.x;
      const dyScreen = event.clientY - dragStartRef.current.y;
      const dx = dxScreen / zoom;
      const dy = dyScreen / zoom;
      let updated = null;
      switch (snap.type) {
        case 'line':
          updated = {
            x1: snap.data.x1 + dx,
            y1: snap.data.y1 + dy,
            x2: snap.data.x2 + dx,
            y2: snap.data.y2 + dy
          };
          break;
        case 'rectangle':
          updated = { x: snap.data.x + dx, y: snap.data.y + dy };
          break;
        case 'circle':
          updated = { cx: snap.data.cx + dx, cy: snap.data.cy + dy };
          break;
        case 'dimension': {
          const x1 = snap.data.x1 + dx;
          const y1 = snap.data.y1 + dy;
          const x2 = snap.data.x2 + dx;
          const y2 = snap.data.y2 + dy;
          const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)).toFixed(1);
          updated = { x1, y1, x2, y2, distance };
          break;
        }
        case 'angle':
          updated = { centerX: snap.data.centerX + dx, centerY: snap.data.centerY + dy };
          break;
        case 'customAngle':
          updated = { centerX: snap.data.centerX + dx, centerY: snap.data.centerY + dy };
          break;
        case 'text':
          updated = { x: snap.data.x + dx, y: snap.data.y + dy };
          break;
        case 'quadrilateral':
          updated = { points: snap.data.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
          break;
        case 'component':
          updated = { x: snap.data.x + dx, y: snap.data.y + dy };
          break;
        case 'baseWall':
          updated = { x: snap.data.x + dx, y: snap.data.y + dy };
          break;
        default:
          return;
      }
      setElements(prev => prev.map(el => el.id === snap.id ? { ...el, ...updated } : el));
      setSelectedElement(prev => prev && prev.id === snap.id ? { ...prev, ...updated } : prev);
      // تحديث baseWall إذا كان العنصر المسحوب هو الحائط الأساسي
      if (snap.type === 'baseWall') {
        setBaseWall(prev => prev && prev.id === snap.id ? { ...prev, ...updated } : prev);
      }
    };
    const handleUp = () => {
      setIsDraggingSelection(false);
      elementDragSnapshotRef.current = null;
      saveToHistory();
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingSelection, zoom, setElements, saveToHistory]);

  // منطق المشاريع (إنشاء/حفظ/تحميل) تمت إزالته بالكامل

  // تبديل الصفحة النشطة
  const switchToPage = useCallback((pageId) => {
    console.log('switchToPage called with:', pageId, 'current:', activePageId);
    console.log('pagesById:', pagesById);
    console.log('pageOrder:', pageOrder);

    if (!pageId || pageId === activePageId) return;

    // لقطة للحالة الحالية للصفحة النشطة
    const currentSnapshot = {
      id: activePageId,
      label: defaultPageLabel(activePageId),
      kind: inferKind(activePageId),
      elements: [...elements],
      layers: { ...layers },
      history: [...history],
      historyIndex,
      zoom,
      pan: { ...pan }
    };

    // الصفحة الهدف (إن وجدت) أو أنشئ صفحة جديدة
    const existing = pagesById?.[pageId];
    const nextPage = existing ? hydratePage(existing, pageId, defaultPageLabel(pageId), inferKind(pageId))
                              : createEmptyPage(pageId, defaultPageLabel(pageId), inferKind(pageId));

    // حدّث كل شيء ذرياً لتجنب حالات السباق
    setPagesById(prev => ({ ...(prev || {}), [activePageId]: currentSnapshot, [pageId]: nextPage }));
    setPageOrder(prev => (Array.isArray(prev) && prev.includes(pageId) ? prev : [ ...(prev || []), pageId ]));
    setActivePageId(pageId);
    loadPageToState(nextPage);
  }, [activePageId, elements, layers, history, historyIndex, zoom, pan, pagesById, pageOrder, loadPageToState]);

  // إضافة جدار جديد
  const addWallPage = useCallback(() => {
    console.log('addWallPage called');
    console.log('Current pageOrder:', pageOrder);
    console.log('Current pagesById:', pagesById);
    
    const existingWalls = pageOrder.filter(id => id.startsWith('wall'));
    const wallNumber = existingWalls.length + 1;
    const newWallId = `wall-${wallNumber}`;
    const newWallPage = createEmptyPage(newWallId, `حائط ${wallNumber}`, 'wall');
    
    console.log('Creating wall:', { newWallId, newWallPage });
    
    setPagesById(prev => ({ ...(prev || {}), [newWallId]: newWallPage }));
    setPageOrder(prev => [...prev, newWallId]);
    
    // التبديل إلى الجدار الجديد
    switchToPage(newWallId);
  }, [pageOrder, pagesById, switchToPage]);

  // حذف جدار
  const removeWallPage = useCallback((wallId) => {
    if (!wallId.startsWith('wall')) return;
    
    const confirmed = window.confirm(`هل تريد حذف ${pagesById?.[wallId]?.label || wallId}؟`);
    if (!confirmed) return;
    
    // إزالة الصفحة
    setPagesById(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      delete updated[wallId];
      return updated;
    });
    setPageOrder(prev => prev.filter(id => id !== wallId));
    
    // إذا كانت الصفحة المحذوفة نشطة، انتقل إلى الأرضية
    if (activePageId === wallId) {
      switchToPage('floor');
    }
  }, [pagesById, activePageId, switchToPage]);


  // حذف المشروع الحالي - تمت إزالته

  // مستمعات أوامر المشاريع من الشريط العلوي تمت إزالتها

  // التكبير
  const handleZoom = useCallback((delta) => {
    // احسب الحد الأدنى للزوم بحيث لا يقل عرض أو ارتفاع الورقة عن عرض أو ارتفاع الشاشة
    const minZoomW = window.innerWidth / 5000;
    const minZoomH = window.innerHeight / 5000;
    const minZoom = Math.max(minZoomW, minZoomH, 0.05);
    const newZoom = Math.max(minZoom, Math.min(5, zoom + delta));
    setZoom(newZoom);
  }, [zoom]);

  // التكبير باستخدام عجلة الفأرة + Shift فقط داخل منطقة الرسم
  const handleWheel = useCallback((event) => {
    if (event.shiftKey) {
      event.preventDefault();
      handleZoom(event.deltaY > 0 ? -0.1 : 0.1);
    }
  }, [handleZoom]);

  // مراجع للتحكم في السحب (التحريك) بزر الفأرة الأيمن
  const panStartMouseRef = useRef({ x: 0, y: 0 });
  const panStartValueRef = useRef({ x: 0, y: 0 });

  // التحقق من وجود رباعي في العناصر
  useEffect(() => {
    const quadExists = elements.some(element => element.type === 'quadrilateral');
    setHasQuadrilateral(quadExists);
  }, [elements]);

  // حساب نقاط الرباعي مع قاعدة أفقية ونقطة تقاطع في المركز
  const calculateQuadrilateralPoints = useCallback((data) => {
    const { sideA, startX, startY } = data;
    
    // تحويل القيم إلى أرقام
    const a = parseFloat(sideA);
    // ملاحظة: القيم الأخرى تُستخدم لاحقاً من كائن data مباشرة عند الإنشاء،
    // لذلك لا نحتاج لتحويلها هنا لتجنب متغيرات غير مستخدمة.
    
    // نقطة تقاطع الأقطار (المركز المحدد)
    const centerX = startX;
    const centerY = startY;
    
    // لضمان أن نقطة تقاطع الأقطار تبقى في المركز المحدد تماماً
    // نحسب النقاط بحيث تتقاطع الأقطار في النقطة (centerX, centerY)
    
    // القاعدة AB أفقية في المنتصف
    const halfBaseWidth = a / 2;
    const baseY = centerY; // القاعدة على نفس مستوى المركز
    
    const finalA = {
      x: centerX - halfBaseWidth,
      y: baseY
    };
    
    const finalB = {
      x: centerX + halfBaseWidth,
      y: baseY
    };
    
    // النقطة C تحسب بحيث القطر AC يمر عبر المركز
    const finalC = {
      x: centerX + (centerX - finalA.x), // نقطة متناظرة مع A حول المركز
      y: centerY + (centerY - finalA.y)
    };
    
    // النقطة D تحسب بحيث القطر BD يمر عبر المركز  
    const finalD = {
      x: centerX + (centerX - finalB.x), // نقطة متناظرة مع B حول المركز
      y: centerY + (centerY - finalB.y)
    };
    
    return [finalA, finalB, finalC, finalD];
  }, []);

  // إنشاء الرباعي
  const createQuadrilateral = useCallback(() => {
    if (!quadrilateralData.sideA || !quadrilateralData.sideB || 
        !quadrilateralData.sideC || !quadrilateralData.sideD ||
        !quadrilateralData.diagonal1 || !quadrilateralData.diagonal2) {
      alert(t.enterAllValues);
      return;
    }

    try {
      // تحويل القيم من mm إلى pixels حسب المقياس (القسمة على MM_PER_PIXEL)
      const convertedData = {
        sideA: parseFloat(quadrilateralData.sideA) / MM_PER_PIXEL,
        sideB: parseFloat(quadrilateralData.sideB) / MM_PER_PIXEL,
        sideC: parseFloat(quadrilateralData.sideC) / MM_PER_PIXEL,
        sideD: parseFloat(quadrilateralData.sideD) / MM_PER_PIXEL,
        diagonal1: parseFloat(quadrilateralData.diagonal1) / MM_PER_PIXEL,
        diagonal2: parseFloat(quadrilateralData.diagonal2) / MM_PER_PIXEL,
        startX: quadrilateralData.startX,
        startY: quadrilateralData.startY
      };
      
      const points = calculateQuadrilateralPoints(convertedData);
      
      const quadrilateral = {
        type: 'quadrilateral',
        id: Date.now(),
        name: 'رباعي',
        points: points,
        sides: {
          a: convertedData.sideA,
          b: convertedData.sideB,
          c: convertedData.sideC,
          d: convertedData.sideD
        },
        diagonals: {
          diagonal1: convertedData.diagonal1,
          diagonal2: convertedData.diagonal2
        },
        layer: 'layer1',
        visible: true
      };

      setElements(prev => [...prev, quadrilateral]);
      setHasQuadrilateral(true);
      setShowQuadrilateralModal(false);
      setQuadrilateralData({
        sideA: '',
        sideB: '',
        sideC: '',
        sideD: '',
        diagonal1: '',
        diagonal2: '',
        startX: 0,
        startY: 0
      });
      saveToHistory();
    } catch (error) {
      alert(t.quadrilateralError);
      console.error('خطأ في حساب الرباعي:', error);
    }
  }, [quadrilateralData, calculateQuadrilateralPoints, saveToHistory, t]);

  // إنشاء الزاوية المخصصة
  const createCustomAngle = useCallback(() => {
    if (!customAngleData.angle || !customAngleData.length1 || !customAngleData.length2 || !customAngleData.lineWidth1 || !customAngleData.lineWidth2) {
      alert(t.enterAllValues);
      return;
    }

    try {
      const angle = parseFloat(customAngleData.angle);
      // تحويل الأطوال من mm إلى pixels حسب المقياس (القسمة على MM_PER_PIXEL)
      const length1 = parseFloat(customAngleData.length1) / MM_PER_PIXEL;
      const length2 = parseFloat(customAngleData.length2) / MM_PER_PIXEL;
      const lineWidth1 = parseFloat(customAngleData.lineWidth1) / MM_PER_PIXEL;
      const lineWidth2 = parseFloat(customAngleData.lineWidth2) / MM_PER_PIXEL;

      const customAngle = {
        type: 'customAngle',
        id: Date.now(),
        name: 'حائط منفرد',
        centerX: customAngleData.startX,
        centerY: customAngleData.startY,
        angle: angle,
        length1: length1,
        length2: length2,
        lineWidth1: lineWidth1,
        lineWidth2: lineWidth2,
        name1: customAngleData.name1 || 'AB',
        name2: customAngleData.name2 || 'AC',
        direction: selectedAngleDirection,
        layer: 'layer1',
        visible: true
      };

      setElements(prev => [...prev, customAngle]);
      setShowCustomAngleModal(false);
      setCustomAngleData({
        angle: '90',
        length1: '1000',
        length2: '1000',
        lineWidth1: '100',
        lineWidth2: '100',
        name1: 'AB',
        name2: 'AC',
        startX: 0,
        startY: 0
      });
      saveToHistory();
    } catch (error) {
      alert(t.customAngleError);
      console.error('خطأ في إنشاء الحائط المنفرد:', error);
    }
  }, [customAngleData, selectedAngleDirection, saveToHistory, t]);

  // التحقق من أن النقطة داخل الحائط الأساسي
  const isPointInsideBaseWall = useCallback((x, y, width = 0, height = 0) => {
    if (!baseWall) return true; // إذا لم يكن هناك حائط أساسي، السماح بالوضع
    
    const wallLeft = Math.min(baseWall.x, baseWall.x + baseWall.width);
    const wallRight = Math.max(baseWall.x, baseWall.x + baseWall.width);
    const wallTop = Math.min(baseWall.y, baseWall.y + baseWall.height);
    const wallBottom = Math.max(baseWall.y, baseWall.y + baseWall.height);
    
    // التحقق من أن جميع زوايا العنصر داخل الحائط
    const elementLeft = x;
    const elementRight = x + width;
    const elementTop = y;
    const elementBottom = y + height;
    
    return elementLeft >= wallLeft && 
           elementRight <= wallRight && 
           elementTop >= wallTop && 
           elementBottom <= wallBottom;
  }, [baseWall]);

  // التعامل مع النقر على العناصر
  const handleElementClick = useCallback((element, event) => {
    event.stopPropagation();
    console.log('تم النقر على العنصر:', element.id, element.type);
    setSelectedElement(element);
    setShowProperties(true);
  }, []);

  // بدء الرسم
  const handleMouseDown = useCallback((event) => {
    // عند الضغط على زر الفأرة الأيمن (right click): اختر أداة التحديد وابدأ التحريك إذا كان هناك عنصر تحت المؤشر
    if (event.button === 2) {
      setSelectedTool('select');
      // البحث عن عنصر تحت المؤشر
      const point = getSVGPoint(event);
      const clickedElement = elements.find(el => {
        if (!el.visible) return false;
        const tolerance = 10;
        if (el.type === 'line') {
          const distToLine = Math.abs((el.y2 - el.y1) * point.x - (el.x2 - el.x1) * point.y + el.x2 * el.y1 - el.y2 * el.x1) /
            Math.sqrt((el.y2 - el.y1) ** 2 + (el.x2 - el.x1) ** 2);
          return distToLine < tolerance;
        } else if (el.type === 'rectangle' || el.type === 'baseWall') {
          return point.x >= el.x - tolerance && point.x <= el.x + el.width + tolerance &&
            point.y >= el.y - tolerance && point.y <= el.y + el.height + tolerance;
        } else if (el.type === 'component') {
          const width = el.width || 0;
          const height = el.height || 0;
          return point.x >= el.x - tolerance && point.x <= el.x + width + tolerance &&
            point.y >= el.y - tolerance && point.y <= el.y + height + tolerance;
        } else if (el.type === 'circle') {
          const dist = Math.sqrt((point.x - el.cx) ** 2 + (point.y - el.cy) ** 2);
          return Math.abs(dist - el.r) < tolerance;
        } else if (el.type === 'text') {
          return point.x >= el.x - tolerance && point.x <= el.x + 100 &&
            point.y >= el.y - 20 && point.y <= el.y + tolerance;
        }
        return false;
      });
      if (clickedElement) {
        setSelectedElement(clickedElement);
        setShowProperties(true);
        setIsDraggingElement(true);
        // حساب الإزاحة بين موضع النقر ومركز العنصر
        if (clickedElement.type === 'line') {
          setDragOffset({ x: point.x - clickedElement.x1, y: point.y - clickedElement.y1 });
        } else if (clickedElement.type === 'rectangle' || clickedElement.type === 'baseWall') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        } else if (clickedElement.type === 'component') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        } else if (clickedElement.type === 'circle') {
          setDragOffset({ x: point.x - clickedElement.cx, y: point.y - clickedElement.cy });
        } else if (clickedElement.type === 'text') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        }
      } else {
        setSelectedElement(null);
        setShowProperties(false);
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // عند الضغط على زر الماوس الأوسط (scroll/middle button)، اختر أداة تحريك الكل وابدأ التحريك فوراً
    if (event.button === 1) {
      setSelectedTool('moveAll');
      if (elements.length > 0) {
        // حفظ مواضع جميع العناصر
        const point = getSVGPoint(event);
        const startPositions = elements.map(el => {
          if (el.type === 'line') {
            return { id: el.id, x1: el.x1, y1: el.y1, x2: el.x2, y2: el.y2 };
          } else if (el.type === 'rectangle' || el.type === 'baseWall' || el.type === 'component') {
            return { id: el.id, x: el.x, y: el.y };
          } else if (el.type === 'circle') {
            return { id: el.id, cx: el.cx, cy: el.cy };
          } else if (el.type === 'text') {
            return { id: el.id, x: el.x, y: el.y };
          } else if (el.type === 'dimension') {
            return { id: el.id, x1: el.x1, y1: el.y1, x2: el.x2, y2: el.y2 };
          } else if (el.type === 'angle' || el.type === 'customAngle') {
            return { id: el.id, centerX: el.centerX, centerY: el.centerY };
          } else if (el.type === 'quadrilateral') {
            return { id: el.id, points: el.points ? el.points.map(p => ({ x: p.x, y: p.y })) : [] };
          }
          return { id: el.id };
        });
        setAllElementsStartPositions(startPositions);
        setIsDraggingAll(true);
        setStartPoint(point);
        setSelectedElement(null);
        setShowProperties(false);
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    let point = getSVGPoint(event);
    // التقاط المغناطيس عند بدء الرسم
    if (magnetEnabled) {
      const nearby = findNearbyPoint(point, elements, null);
      if (nearby) point = nearby;
    }
    
    // إذا كانت أداة تحريك الكل نشطة
    if (selectedTool === 'moveAll') {
      if (elements.length === 0) return;
      
      // حفظ المواضع الحالية لجميع العناصر
      const startPositions = elements.map(el => {
        if (el.type === 'line') {
          return { id: el.id, x1: el.x1, y1: el.y1, x2: el.x2, y2: el.y2 };
        } else if (el.type === 'rectangle' || el.type === 'baseWall' || el.type === 'component') {
          return { id: el.id, x: el.x, y: el.y };
        } else if (el.type === 'circle') {
          return { id: el.id, cx: el.cx, cy: el.cy };
        } else if (el.type === 'text') {
          return { id: el.id, x: el.x, y: el.y };
        } else if (el.type === 'dimension') {
          return { id: el.id, x1: el.x1, y1: el.y1, x2: el.x2, y2: el.y2 };
        } else if (el.type === 'angle' || el.type === 'customAngle') {
          return { id: el.id, centerX: el.centerX, centerY: el.centerY };
        } else if (el.type === 'quadrilateral') {
          return { id: el.id, points: el.points ? el.points.map(p => ({ x: p.x, y: p.y })) : [] };
        }
        return { id: el.id };
      });
      
      setAllElementsStartPositions(startPositions);
      setIsDraggingAll(true);
      setStartPoint(point);
      setSelectedElement(null);
      setShowProperties(false);
      return;
    }
    
    // إذا كانت أداة التحديد نشطة
    if (selectedTool === 'select') {
      // البحث عن عنصر تم النقر عليه
      const clickedElement = elements.find(el => {
        if (!el.visible) return false;
        
        const tolerance = 10;
        
        if (el.type === 'line') {
          const distToLine = Math.abs((el.y2 - el.y1) * point.x - (el.x2 - el.x1) * point.y + el.x2 * el.y1 - el.y2 * el.x1) / 
                            Math.sqrt((el.y2 - el.y1) ** 2 + (el.x2 - el.x1) ** 2);
          return distToLine < tolerance;
        } else if (el.type === 'rectangle' || el.type === 'baseWall') {
          return point.x >= el.x - tolerance && point.x <= el.x + el.width + tolerance &&
                 point.y >= el.y - tolerance && point.y <= el.y + el.height + tolerance;
        } else if (el.type === 'component') {
          const width = el.width || 0;
          const height = el.height || 0;
          return point.x >= el.x - tolerance && point.x <= el.x + width + tolerance &&
                 point.y >= el.y - tolerance && point.y <= el.y + height + tolerance;
        } else if (el.type === 'circle') {
          const dist = Math.sqrt((point.x - el.cx) ** 2 + (point.y - el.cy) ** 2);
          return Math.abs(dist - el.r) < tolerance;
        } else if (el.type === 'text') {
          return point.x >= el.x - tolerance && point.x <= el.x + 100 &&
                 point.y >= el.y - 20 && point.y <= el.y + tolerance;
        }
        return false;
      });
      
      if (clickedElement) {
        setSelectedElement(clickedElement);
        setShowProperties(true);
        setIsDraggingElement(true);
        
        // حساب الإزاحة بين موضع النقر ومركز العنصر
        if (clickedElement.type === 'line') {
          setDragOffset({ x: point.x - clickedElement.x1, y: point.y - clickedElement.y1 });
        } else if (clickedElement.type === 'rectangle' || clickedElement.type === 'baseWall') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        } else if (clickedElement.type === 'component') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        } else if (clickedElement.type === 'circle') {
          setDragOffset({ x: point.x - clickedElement.cx, y: point.y - clickedElement.cy });
        } else if (clickedElement.type === 'text') {
          setDragOffset({ x: point.x - clickedElement.x, y: point.y - clickedElement.y });
        }
      } else {
        setSelectedElement(null);
        setShowProperties(false);
      }
      return;
    }
    
    setStartPoint(point);
    let element = null;
    let instant = false;
    
    switch (selectedTool) {
      case 'wall':
        element = {
          type: 'wall',
          id: Date.now(),
          name: t.wall,
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y,
          thickness: 10, // افتراضي 100 مم (10 بيكسل)
          layer: 'layer1',
          visible: true
        };
        break;
      case 'line':
        element = {
          type: 'line',
          id: Date.now(),
          name: 'خط',
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'rectangle':
        element = {
          type: 'rectangle',
          id: Date.now(),
          name: 'مستطيل',
          x: point.x,
          y: point.y,
          width: 0,
          height: 0,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'component':
        // فتح نافذة الإدخال بدلاً من الرسم المباشر
        if (!baseWall) {
          alert(t.mustDrawBaseWall);
          return;
        }
        setShowComponentModal(true);
        return;
      case 'circle':
        element = {
          type: 'circle',
          id: Date.now(),
          name: 'دائرة',
          cx: point.x,
          cy: point.y,
          r: 0,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'dimension':
        element = {
          type: 'dimension',
          id: Date.now(),
          name: 'قياس',
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y,
          distance: 0,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'angle':
        element = {
          type: 'angle',
          id: Date.now(),
          name: 'زاوية',
          centerX: point.x,
          centerY: point.y,
          length: 100,
          angle: 90,
          direction: selectedAngleDirection,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'text':
        element = {
          type: 'text',
          id: Date.now(),
          name: 'نص',
          x: point.x,
          y: point.y,
          text: 'نص',
          fontSize: 16,
          layer: 'layer1',
          visible: true
        };
        instant = true;
        break;
      case 'baseWall':
        if (baseWall) {
          alert(t.baseWallAlreadyDrawn);
          return;
        }
        element = {
          type: 'baseWall',
          id: Date.now(),
          name: 'الحائط الأساسي',
          x: point.x,
          y: point.y,
          width: 0,
          height: 0,
          layer: 'layer1',
          visible: true
        };
        break;
      case 'customAngle':
        // فتح نافذة الإدخال لتحديد قياسات الزاوية المخصصة
        setCustomAngleData({
          angle: '90',
          length1: '100',
          length2: '100',
          lineWidth1: '10',
          lineWidth2: '10',
          name1: 'AB',
          name2: 'AC',
          startX: point.x,
          startY: point.y
        });
        setShowCustomAngleModal(true);
        return;
      default:
        break;
    }
    
    if (element) {
      if (instant) {
        setElements(prev => [...prev, element]);
        setSelectedElement(element);
        setShowProperties(true);
        if (element.type === 'text') {
          setPendingTextId(element.id);
          setTextValue('');
          setTextSizeValue('16');
          setShowTextModal(true);
        }
      } else {
        setIsDrawing(true);
        setElements(prev => [...prev, element]);
        setDrawingElementId(element.id);
        setCurrentElement(null);
      }
    }
  }, [selectedTool, getSVGPoint, selectedAngleDirection, pan, saveToHistory, selectedComponentLabel, getComponentColor]);

  // تحديث الرسم أثناء الحركة
  const handleMouseMove = useCallback((event) => {
    const point = getSVGPoint(event);
    setMousePosition(point);
    // إذا كنا في وضع التحريك بزر الفأرة الأيمن
    if (isPanning) {
      const dx = event.clientX - panStartMouseRef.current.x;
      const dy = event.clientY - panStartMouseRef.current.y;
      setPan({
        x: panStartValueRef.current.x + dx,
        y: panStartValueRef.current.y + dy
      });
      return;
    }
    
    // تحريك جميع العناصر
    if (isDraggingAll && allElementsStartPositions.length > 0) {
      const dx = point.x - startPoint.x;
      const dy = point.y - startPoint.y;
      
      setElements(prev => prev.map(el => {
        const startPos = allElementsStartPositions.find(pos => pos.id === el.id);
        if (!startPos) return el;
        
        const updated = { ...el };
        
        if (el.type === 'line') {
          updated.x1 = startPos.x1 + dx;
          updated.y1 = startPos.y1 + dy;
          updated.x2 = startPos.x2 + dx;
          updated.y2 = startPos.y2 + dy;
        } else if (el.type === 'rectangle' || el.type === 'baseWall' || el.type === 'component' || el.type === 'text') {
          updated.x = startPos.x + dx;
          updated.y = startPos.y + dy;
        } else if (el.type === 'circle') {
          updated.cx = startPos.cx + dx;
          updated.cy = startPos.cy + dy;
        } else if (el.type === 'dimension') {
          updated.x1 = startPos.x1 + dx;
          updated.y1 = startPos.y1 + dy;
          updated.x2 = startPos.x2 + dx;
          updated.y2 = startPos.y2 + dy;
        } else if (el.type === 'angle' || el.type === 'customAngle') {
          updated.centerX = startPos.centerX + dx;
          updated.centerY = startPos.centerY + dy;
        } else if (el.type === 'quadrilateral' && startPos.points) {
          updated.points = startPos.points.map(p => ({
            x: p.x + dx,
            y: p.y + dy
          }));
        }
        
        return updated;
      }));
      
      // تحديث baseWall إذا كان موجوداً
      if (baseWall) {
        const baseWallStartPos = allElementsStartPositions.find(pos => pos.id === baseWall.id);
        if (baseWallStartPos) {
          setBaseWall(prev => ({
            ...prev,
            x: baseWallStartPos.x + dx,
            y: baseWallStartPos.y + dy
          }));
        }
      }
      
      return;
    }
    
    // سحب العنصر المحدد في وضع التحديد
    if (isDraggingElement && selectedElement && selectedTool === 'select') {
      const newX = point.x - dragOffset.x;
      const newY = point.y - dragOffset.y;
      
      // التحقق من أن العنصر من نوع component والتحقق من الحدود (عدا الباب الأرضي)
      if (selectedElement.type === 'component' && selectedElement.originalType !== 'floorDoor') {
        const width = selectedElement.width || 0;
        const height = selectedElement.height || 0;
        
        // إذا كان خارج الحدود، لا نقوم بالتحديث
        if (!isPointInsideBaseWall(newX, newY, width, height)) {
          // إظهار تحذير مؤقت
          setShowBoundaryWarning(true);
          setTimeout(() => setShowBoundaryWarning(false), 100);
          return;
        }
      }
      
      setElements(prev => prev.map(el => {
        if (el.id !== selectedElement.id) return el;
        
        const updated = { ...el };
        if (el.type === 'line') {
          const dx = newX - el.x1;
          const dy = newY - el.y1;
          updated.x1 = newX;
          updated.y1 = newY;
          updated.x2 = el.x2 + dx;
          // لا داعي لـ baseWall بعد الآن
        } else if (el.type === 'circle') {
          updated.cx = newX;
          updated.cy = newY;
        } else if (el.type === 'text') {
          updated.x = newX;
          updated.y = newY;
        } else if (el.type === 'baseWall') {
          updated.x = newX;
          updated.y = newY;
        } else if (el.type === 'dimension') {
          const dx = newX - el.x1;
          const dy = newY - el.y1;
          updated.x1 = newX;
          updated.y1 = newY;
          updated.x2 = el.x2 + dx;
          updated.y2 = el.y2 + dy;
        } else if (el.type === 'angle') {
          updated.centerX = newX;
          updated.centerY = newY;
        } else if (el.type === 'customAngle') {
          updated.centerX = newX;
          updated.centerY = newY;
        } else if (el.type === 'component') {
          updated.x = newX;
          updated.y = newY;
        }
        return updated;
      }));
      
      // تحديث العنصر المحدد
      setSelectedElement(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (prev.type === 'line') {
          const dx = newX - prev.x1;
          const dy = newY - prev.y1;
          updated.x1 = newX;
          updated.y1 = newY;
          updated.x2 = prev.x2 + dx;
          updated.y2 = prev.y2 + dy;
        } else if (prev.type === 'rectangle') {
          updated.x = newX;
          updated.y = newY;
        } else if (prev.type === 'circle') {
          updated.cx = newX;
          updated.cy = newY;
        } else if (prev.type === 'text') {
          updated.x = newX;
          updated.y = newY;
        } else if (prev.type === 'baseWall') {
          updated.x = newX;
          updated.y = newY;
        } else if (prev.type === 'dimension') {
          const dx = newX - prev.x1;
          const dy = newY - prev.y1;
          updated.x1 = newX;
          updated.y1 = newY;
          updated.x2 = prev.x2 + dx;
          updated.y2 = prev.y2 + dy;
        } else if (prev.type === 'angle') {
          updated.centerX = newX;
          updated.centerY = newY;
        } else if (prev.type === 'customAngle') {
          updated.centerX = newX;
          updated.centerY = newY;
        } else if (prev.type === 'component') {
          updated.x = newX;
          updated.y = newY;
        }
        return updated;
      });
      return;
    }
    
    // سحب النقاط في وضع التحديد
    if (selectedElement && selectedElement.draggingPoint && selectedTool === 'select') {
      return;
    }
    
    // البحث عن أقرب نقطة للالتصاق
    const nearbyPoint = findNearbyPoint(point, elements, drawingElementId);
    setSnapPoint(nearbyPoint);
    
    // تحديث العنصر الجاري رسمه داخل elements مباشرةً
    if (!isDrawing || !drawingElementId) return;
    
    // استخدام النقطة الملتصقة إذا وجدت، وإلا استخدام النقطة الحالية
    const currentPoint = nearbyPoint || point;
    
    setElements(prev => prev.map(el => {
      if (el.id !== drawingElementId) return el;
      const updated = { ...el };
      switch (el.type) {
        case 'wall':
          updated.x2 = currentPoint.x;
          updated.y2 = currentPoint.y;
          break;
        case 'line':
          updated.x2 = currentPoint.x;
          updated.y2 = currentPoint.y;
          break;
        case 'rectangle':
          updated.width = currentPoint.x - startPoint.x;
          updated.height = currentPoint.y - startPoint.y;
          break;
        case 'component':
          // تم تعطيل الرسم بالفأرة للمكونات
          break;
        case 'baseWall':
          updated.width = currentPoint.x - startPoint.x;
          updated.height = currentPoint.y - startPoint.y;
          break;
        case 'circle': {
          const dx = currentPoint.x - startPoint.x;
          const dy = currentPoint.y - startPoint.y;
          updated.r = Math.sqrt(dx * dx + dy * dy);
          break;
        }
        case 'dimension': {
          updated.x2 = currentPoint.x;
          updated.y2 = currentPoint.y;
          const distance = Math.sqrt(
            Math.pow(currentPoint.x - startPoint.x, 2) + Math.pow(currentPoint.y - startPoint.y, 2)
          );
          updated.distance = distance.toFixed(1);
          break;
        }
        case 'angle': {
          const dxAngle = currentPoint.x - updated.centerX;
          const dyAngle = currentPoint.y - updated.centerY;
          updated.length = Math.sqrt(dxAngle * dxAngle + dyAngle * dyAngle);
          // تحديث الاتجاه أثناء السحب لالتقاط تغيير المستخدم في الإعدادات
          updated.direction = selectedAngleDirection;
          break;
        }
        default:
          break;
      }
      return updated;
    }));
  }, [isDrawing, drawingElementId, startPoint, getSVGPoint, selectedElement, selectedTool, selectedAngleDirection, setElements, elements, findNearbyPoint, isDraggingElement, dragOffset, isPointInsideBaseWall, setShowBoundaryWarning]);

  // إنهاء الرسم
  const handleMouseUp = useCallback(() => {
    // إيقاف تحريك جميع العناصر
    if (isDraggingAll) {
      setIsDraggingAll(false);
      setAllElementsStartPositions([]);
      saveToHistory();
      return;
    }
    
    // إيقاف سحب العنصر
    if (isDraggingElement) {
      setIsDraggingElement(false);
      saveToHistory();
      return;
    }
    
    // إخفاء نقطة الالتصاق عند الانتهاء من الرسم
    setSnapPoint(null);
    
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    
    // إيقاف سحب النقاط
    if (selectedElement && selectedElement.draggingPoint) {
      const updatedElement = { ...selectedElement };
      delete updatedElement.draggingPoint;
      setSelectedElement(updatedElement);
      return;
    }
    
    if (!isDrawing || !drawingElementId) return;
    
    const drawn = elements.find(el => el.id === drawingElementId);
    if (!drawn) {
      setIsDrawing(false);
      setDrawingElementId(null);
      setCurrentElement(null);
      return;
    }
    
    if (drawn.type === 'baseWall') {
      setBaseWall(drawn);
      setIsDrawing(false);
      setDrawingElementId(null);
      setCurrentElement(null);
      saveToHistory();
      return;
    }
    
    if (drawn.type === 'angle') {
      setPendingAngleId(drawingElementId);
      setShowAngleModal(true);
      setAngleValue(String(drawn.angle ?? 90));
      setIsDrawing(false);
      setDrawingElementId(null);
      setCurrentElement(null);
      return;
    }
    
    setIsDrawing(false);
    setDrawingElementId(null);
    setCurrentElement(null);
    saveToHistory();
  }, [isPanning, selectedElement, isDrawing, drawingElementId, elements, saveToHistory]);

  // مستمعات عامة للماوس لضمان استمرار السحب خارج حدود الـ SVG
  useEffect(() => {
    if (!isPanning) return;
    const handleGlobalMove = (e) => {
      // إذا لم يعد الزر الأيمن (2) أو الأوسط (4) مضغوطاً إنهاء التحريك
      if (!(e.buttons & 2) && !(e.buttons & 4)) {
        setIsPanning(false);
        return;
      }
      const dx = e.clientX - panStartMouseRef.current.x;
      const dy = e.clientY - panStartMouseRef.current.y;
      setPan({
        x: panStartValueRef.current.x + dx,
        y: panStartValueRef.current.y + dy
      });
    };
    const handleGlobalUp = () => setIsPanning(false);
    const preventContext = (e) => e.preventDefault();
    window.addEventListener('mousemove', handleGlobalMove);
    window.addEventListener('mouseup', handleGlobalUp);
    window.addEventListener('contextmenu', preventContext);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMove);
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('contextmenu', preventContext);
    };
  }, [isPanning]);

  // دالة لحساب خطوط المسافة بين عنصر والحائط الأساسي
  const calculateDistanceLines = useCallback((element) => {
    if (!baseWall || element.type === 'baseWall' || element.type === 'text') return [];
    
    const wallX = baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x;
    const wallY = baseWall.height < 0 ? baseWall.y + baseWall.height : baseWall.y;
    const wallW = Math.abs(baseWall.width);
    const wallH = Math.abs(baseWall.height);
    
    const lines = [];
    
    // استخراج أضلاع العنصر ورسم خطوط عمودية منها
    if (element.type === 'rectangle' || element.type === 'component') {
      const ex = element.width < 0 ? element.x + element.width : element.x;
      const ey = element.height < 0 ? element.y + element.height : element.y;
      const ew = Math.abs(element.width);
      const eh = Math.abs(element.height);
      
      // الضلع العلوي - خط عمودي للأعلى
      const topMidX = ex + ew / 2;
      const topMidY = ey;
      if (topMidY > wallY) {
        // الخط يذهب للأعلى إلى الضلع العلوي للحائط
        lines.push({
          x1: topMidX,
          y1: topMidY,
          x2: topMidX,
          y2: wallY,
          distance: (Math.abs(topMidY - wallY) * MM_PER_PIXEL).toFixed(1),
          midX: topMidX,
          midY: (topMidY + wallY) / 2
        });
      }
      
      // الضلع السفلي - خط عمودي للأسفل
      const bottomMidX = ex + ew / 2;
      const bottomMidY = ey + eh;
      if (bottomMidY < wallY + wallH) {
        // الخط يذهب للأسفل إلى الضلع السفلي للحائط
        lines.push({
          x1: bottomMidX,
          y1: bottomMidY,
          x2: bottomMidX,
          y2: wallY + wallH,
          distance: (Math.abs(bottomMidY - (wallY + wallH)) * MM_PER_PIXEL).toFixed(1),
          midX: bottomMidX,
          midY: (bottomMidY + wallY + wallH) / 2
        });
      }
      
      // الضلع الأيسر - خط أفقي لليسار
      const leftMidX = ex;
      const leftMidY = ey + eh / 2;
      if (leftMidX > wallX) {
        // الخط يذهب لليسار إلى الضلع الأيسر للحائط
        lines.push({
          x1: leftMidX,
          y1: leftMidY,
          x2: wallX,
          y2: leftMidY,
          distance: (Math.abs(leftMidX - wallX) * MM_PER_PIXEL).toFixed(1),
          midX: (leftMidX + wallX) / 2,
          midY: leftMidY
        });
      }
      
      // الضلع الأيمن - خط أفقي لليمين
      const rightMidX = ex + ew;
      const rightMidY = ey + eh / 2;
      if (rightMidX < wallX + wallW) {
        // الخط يذهب لليمين إلى الضلع الأيمن للحائط
        lines.push({
          x1: rightMidX,
          y1: rightMidY,
          x2: wallX + wallW,
          y2: rightMidY,
          distance: (Math.abs(rightMidX - (wallX + wallW)) * MM_PER_PIXEL).toFixed(1),
          midX: (rightMidX + wallX + wallW) / 2,
          midY: rightMidY
        });
      }
    } else if (element.type === 'circle') {
      const cx = element.cx;
      const cy = element.cy;
      const r = element.r;
      
      // أعلى الدائرة - خط عمودي للأعلى
      if (cy - r > wallY) {
        lines.push({
          x1: cx,
          y1: cy - r,
          x2: cx,
          y2: wallY,
          distance: (Math.abs(cy - r - wallY) * MM_PER_PIXEL).toFixed(1),
          midX: cx,
          midY: (cy - r + wallY) / 2
        });
      }
      
      // أسفل الدائرة - خط عمودي للأسفل
      if (cy + r < wallY + wallH) {
        lines.push({
          x1: cx,
          y1: cy + r,
          x2: cx,
          y2: wallY + wallH,
          distance: (Math.abs(cy + r - (wallY + wallH)) * MM_PER_PIXEL).toFixed(1),
          midX: cx,
          midY: (cy + r + wallY + wallH) / 2
        });
      }
      
      // يسار الدائرة - خط أفقي لليسار
      if (cx - r > wallX) {
        lines.push({
          x1: cx - r,
          y1: cy,
          x2: wallX,
          y2: cy,
          distance: (Math.abs(cx - r - wallX) * MM_PER_PIXEL).toFixed(1),
          midX: (cx - r + wallX) / 2,
          midY: cy
        });
      }
      
      // يمين الدائرة - خط أفقي لليمين
      if (cx + r < wallX + wallW) {
        lines.push({
          x1: cx + r,
          y1: cy,
          x2: wallX + wallW,
          y2: cy,
          distance: (Math.abs(cx + r - (wallX + wallW)) * MM_PER_PIXEL).toFixed(1),
          midX: (cx + r + wallX + wallW) / 2,
          midY: cy
        });
      }
    }
    
    return lines;
  }, [baseWall]);

  // رسم الشبكة
  const renderGrid = () => {
    const gridLines = [];
    const width = 1500;
    const height = 1500;
    
    for (let x = 0; x <= width; x += gridSize) {
      gridLines.push(
        <line key={`v-${x}`} x1={x} y1={0} x2={x} y2={height} stroke="#e0e0e0" strokeWidth="0.5" />
      );
    }
    for (let y = 0; y <= height; y += gridSize) {
      gridLines.push(
        <line key={`h-${y}`} x1={0} y1={y} x2={width} y2={y} stroke="#e0e0e0" strokeWidth="0.5" />
      );
    }
    return gridLines;
  };

  // رسم العناصر
  const renderElement = (element, isSelected = false, providedLayer, elementIndex = null) => {
    // التأكد من وجود layer صحيح - حل نهائي للمشكلة
    let elementLayer;
    if (providedLayer) {
      elementLayer = providedLayer;
    } else if (element.layer && layers[element.layer]) {
      elementLayer = layers[element.layer];
    } else if (layers.layer1) {
      elementLayer = layers.layer1;
    } else {
      // قيمة احتياطية نهائية
      elementLayer = { color: '#3498db', name: 'افتراضي', visible: true };
    }
    
    const commonProps = {
      key: element.id,
      stroke: elementLayer?.color || '#000000',
      strokeWidth: 2,
      fill: 'none',
      strokeLinejoin: 'miter', // زوايا حادة
      strokeLinecap: 'square', // نهايات مربعة حادة
      style: { cursor: 'crosshair' }
    };
    
    switch (element.type) {
      case 'line':
        return (
          <line
            {...commonProps}
            x1={element.x1}
            y1={element.y1}
            x2={element.x2}
            y2={element.y2}
          />
        );
      case 'wall': {
        // حساب متجه الوحدة العمودي على الجدار
        const dx = element.x2 - element.x1;
        const dy = element.y2 - element.y1;
        const len = Math.sqrt(dx*dx + dy*dy) || 1;
        const ux = dx / len;
        const uy = dy / len;
        // متجه عمودي (لليمين)
        const nx = -uy;
        const ny = ux;
        const t = element.thickness || 10;
        // حساب رؤوس المضلع (مستطيل الجدار)
        const p1x = element.x1 + nx * t/2;
        const p1y = element.y1 + ny * t/2;
        const p2x = element.x2 + nx * t/2;
        const p2y = element.y2 + ny * t/2;
        const p3x = element.x2 - nx * t/2;
        const p3y = element.y2 - ny * t/2;
        const p4x = element.x1 - nx * t/2;
        const p4y = element.y1 - ny * t/2;
        const midX = (element.x1 + element.x2) / 2;
        const midY = (element.y1 + element.y2) / 2;
        return (
          <g key={element.id} style={{ cursor: 'crosshair' }}>
            <polygon
              points={`${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y} ${p4x},${p4y}`}
              fill="#e0e0e0"
              stroke={elementLayer?.color || '#000000'}
              strokeWidth={2}
            />
            {/* نص الطول */}
            <text
              x={midX}
              y={midY - 12}
              fill={'#222'}
              fontSize="14"
              fontWeight="bold"
              textAnchor="middle"
              fontFamily="Arial, sans-serif"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {Math.round(len * MM_PER_PIXEL) + ' mm'}
            </text>
            {/* نص السمك */}
            <text
              x={midX + nx * (t/2 + 32)}
              y={midY + ny * (t/2 + 32)}
              fill="#388e3c"
              fontSize="12"
              fontWeight="bold"
              textAnchor="middle"
              fontFamily="Arial, sans-serif"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {t * MM_PER_PIXEL + ' mm'}
            </text>
          </g>
        );
      }
      case 'rectangle':
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            <rect
              {...commonProps}
              x={element.width < 0 ? element.x + element.width : element.x}
              y={element.height < 0 ? element.y + element.height : element.y}
              width={Math.abs(element.width)}
              height={Math.abs(element.height)}
              fill={isSelected ? '#ffe082' : '#90caf9'}
              stroke={isSelected ? '#ff0000' : (elementLayer?.color || '#1976d2')}
              strokeWidth={isSelected ? 3 : 2}
              fillOpacity={0.5}
            />
          </g>
        );
      case 'baseWall': {
        const x = element.width < 0 ? element.x + element.width : element.x;
        const y = element.height < 0 ? element.y + element.height : element.y;
        const w = Math.abs(element.width);
        const h = Math.abs(element.height);
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill="none"
              stroke={isSelected ? '#ff0000' : '#2c3e50'}
              strokeWidth={isSelected ? '4' : '3'}
              strokeDasharray="10,5"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
            
            {/* طول الضلع العلوي */}
            {/* خلفية بيضاء للنص */}
            <rect
              x={x + w / 2 - 28}
              y={y - 24}
              width={56}
              height={20}
              fill="#fff"
              opacity={0.85}
              rx={4}
              ry={4}
              style={{ pointerEvents: 'none' }}
            />
            <text
              x={x + w / 2}
              y={y - 10}
              fill="#2c3e50"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
              textAnchor="middle"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {Math.round(w * MM_PER_PIXEL)} mm
            </text>
            
            {/* طول الضلع السفلي */}
            <rect
              x={x + w / 2 - 28}
              y={y + h + 8}
              width={56}
              height={20}
              fill="#fff"
              opacity={0.85}
              rx={4}
              ry={4}
              style={{ pointerEvents: 'none' }}
            />
            <text
              x={x + w / 2}
              y={y + h + 22}
              fill="#2c3e50"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
              textAnchor="middle"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {Math.round(w * MM_PER_PIXEL)} mm
            </text>
            
            {/* طول الضلع الأيسر */}
            <rect
              x={x - 43}
              y={y + h / 2 - 10}
              width={56}
              height={20}
              fill="#fff"
              opacity={0.85}
              rx={4}
              ry={4}
              style={{ pointerEvents: 'none' }}
            />
            <text
              x={x - 15}
              y={y + h / 2 + 5}
              fill="#2c3e50"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
              textAnchor="middle"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {Math.round(h * MM_PER_PIXEL)} mm
            </text>
            
            {/* طول الضلع الأيمن */}
            <rect
              x={x + w + 1}
              y={y + h / 2 - 10}
              width={56}
              height={20}
              fill="#fff"
              opacity={0.85}
              rx={4}
              ry={4}
              style={{ pointerEvents: 'none' }}
            />
            <text
              x={x + w + 15}
              y={y + h / 2 + 5}
              fill="#2c3e50"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
              textAnchor="middle"
              paintOrder="stroke"
              stroke="#fff"
              strokeWidth="0.7"
              style={{ pointerEvents: 'none' }}
            >
              {Math.round(h * MM_PER_PIXEL)} mm
            </text>
          </g>
        );
      }
      case 'component': {
        const x = element.width < 0 ? element.x + element.width : element.x;
        const y = element.height < 0 ? element.y + element.height : element.y;
        const w = Math.abs(element.width);
        const h = Math.abs(element.height);
        const fc = String(element.fill || '#000000').toLowerCase();
        const textFill = (fc === '#f1c40f' || fc === 'yellow' || fc === '#ffffff' || fc === 'white') ? '#000000' : '#ffffff';
        
        // إذا كان الباب الأرضي، رسم خاص حسب الاتجاه
        if (element.originalType === 'floorDoor') {
          // دعم جميع الاتجاهات الأربعة
          const direction = element.direction || 'bottom-left';
          let line1Props, line2Props, arcPath, fillPath, textPos;
          // دعم 8 نماذج: 4 عادية و4 معكوسة (تبديل الألوان)
          let flip = false;
          let baseDir = direction;
          if (direction.endsWith('-flip')) {
            flip = true;
            baseDir = direction.replace('-flip', '');
          }
          if (baseDir === 'bottom-left') {
            // عمودي يسار، أفقي أسفل
            line1Props = { x1: x, y1: y, x2: x, y2: y + h }; // عمودي
            line2Props = { x1: x, y1: y + h, x2: x + w, y2: y + h }; // أفقي
            arcPath = `M ${x} ${y} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 1 ${x + w} ${y + h}`;
            fillPath = `M ${x} ${y} L ${x} ${y + h} L ${x + w} ${y + h} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 0 ${x} ${y} Z`;
            textPos = { x: x + w / 3, y: y + h - h / 3 };
          } else if (baseDir === 'bottom-right') {
            line1Props = { x1: x + w, y1: y, x2: x + w, y2: y + h };
            line2Props = { x1: x, y1: y + h, x2: x + w, y2: y + h };
            arcPath = `M ${x + w} ${y} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 0 ${x} ${y + h}`;
            fillPath = `M ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 1 ${x + w} ${y} Z`;
            textPos = { x: x + 2 * w / 3, y: y + h - h / 3 };
          } else if (baseDir === 'top-left') {
            line1Props = { x1: x, y1: y, x2: x, y2: y + h };
            line2Props = { x1: x, y1: y, x2: x + w, y2: y };
            arcPath = `M ${x} ${y + h} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 0 ${x + w} ${y}`;
            fillPath = `M ${x} ${y + h} L ${x} ${y} L ${x + w} ${y} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 1 ${x} ${y + h} Z`;
            textPos = { x: x + w / 3, y: y + h / 3 };
          } else if (baseDir === 'top-right') {
            line1Props = { x1: x + w, y1: y, x2: x + w, y2: y + h };
            line2Props = { x1: x, y1: y, x2: x + w, y2: y };
            arcPath = `M ${x + w} ${y + h} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 1 ${x} ${y}`;
            fillPath = `M ${x + w} ${y + h} L ${x + w} ${y} L ${x} ${y} A ${Math.min(w, h)} ${Math.min(w, h)} 0 0 0 ${x + w} ${y + h} Z`;
            textPos = { x: x + 2 * w / 3, y: y + h / 3 };
          }
          
          return (
            <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
              {/* الضلع العمودي (أبيض أو أحمر) */}
              <line
                {...line1Props}
                stroke={isSelected ? '#ff0000' : (flip ? '#ff0000' : '#fff')}
                strokeWidth={isSelected ? 5 : 4}
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
              {/* الضلع الأفقي (أحمر أو أبيض) */}
              <line
                {...line2Props}
                stroke={isSelected ? '#ff0000' : (flip ? '#fff' : '#ff0000')}
                strokeWidth={isSelected ? 4 : 3}
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
              {/* القوس الدائري */}
              <path
                d={arcPath}
                stroke={isSelected ? '#ff0000' : '#111827'}
                strokeWidth={isSelected ? 2 : 1}
                fill="none"
                strokeDasharray="3,3"
              />
              {/* مساحة ملونة */}
              <path
                d={fillPath}
                fill={element.fill || '#ffffff'}
                opacity="0.3"
              />
              <text
                x={textPos.x}
                y={textPos.y}
                fill={textFill}
                fontSize={Math.max(10, Math.min(16, Math.min(w, h) / 4))}
                fontFamily="Arial, sans-serif"
                textAnchor="middle"
                alignmentBaseline="middle"
                style={{ pointerEvents: 'none' }}
              >
                {element.label || 'FD'}
              </text>
            </g>
          );
        }
        
        // الرسم العادي للمكونات الأخرى
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill={element.fill || '#000000'}
              stroke={isSelected ? '#ff0000' : '#111827'}
              strokeWidth={isSelected ? 3 : 2}
              strokeLinejoin="miter"
              strokeLinecap="square"
            />
            <text
              x={x + w / 2}
              y={y + h / 2}
              fill={textFill}
              fontSize={Math.max(12, Math.min(20, Math.min(w, h) / 3))}
              fontFamily="Arial, sans-serif"
              textAnchor="middle"
              alignmentBaseline="middle"
              style={{ pointerEvents: 'none' }}
            >
              {element.label || 'مكون'}
            </text>
          </g>
        );
      }
      case 'circle':
        return (
          <circle
            {...commonProps}
            cx={element.cx}
            cy={element.cy}
            r={Math.abs(element.r)}
          />
        );
      case 'text':
        return (
          <text
            key={element.id}
            x={element.x}
            y={element.y}
            fill={'#000000'}
            fontSize={element.fontSize || 16}
            fontFamily="Arial, sans-serif"
            onClick={(event) => handleElementClick(element, event)}
            style={{ cursor: 'text' }}
          >
            {element.text || 'نص'}
          </text>
        );
      case 'dimension':
        const midX = (element.x1 + element.x2) / 2;
        const midY = (element.y1 + element.y2) / 2;
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            <line
              stroke={isSelected ? '#ff0000' : '#000000'}
              strokeWidth={isSelected ? '3' : '2'}
              fill="none"
              x1={element.x1}
              y1={element.y1}
              x2={element.x2}
              y2={element.y2}
              strokeDasharray="5,5"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
            <text
              x={midX}
              y={midY - 5}
              fill={isSelected ? '#ff0000' : '#000000'}
              fontSize="12"
              textAnchor="middle"
              fontFamily="Arial"
            >
              {Math.round(element.distance * MM_PER_PIXEL)} mm
            </text>
          </g>
        );
      case 'angle':
        // الزاوية تُقاس من الخط الأفقي AB إلى الأعلى
        // استخدام زاوية افتراضية 90 درجة أثناء الرسم إذا لم تكن محددة
        const currentAngle = element.angle || 90;
        const angleRad = (currentAngle * Math.PI) / 180;
        
        // استخدام الطول الفعلي أو قيمة افتراضية
        const displayLength = element.length || 100;
        
        // النقطة A (المركز)
        const pointA = { x: element.centerX, y: element.centerY };
        
        // تحديد اتجاه الخط AB حسب اتجاه الزاوية باستخدام زاوية أساس α وزاوية نسبية φ
        const direction = element.direction || 'horizontal_right_up';
        const isRight = direction.includes('right');
        const isUp = direction.includes('up');
        const alpha = isRight ? 0 : Math.PI; // 0 لليمين، π لليسار
        // تعيين إشارة φ بحسب الاتجاه: يمين/فوق = -, يمين/أسفل = +, يسار/فوق = +, يسار/أسفل = -
        const phi = (isRight ? (isUp ? -1 : 1) : (isUp ? 1 : -1)) * angleRad;

        const pointB = {
          x: element.centerX + displayLength * Math.cos(alpha),
          y: element.centerY + displayLength * Math.sin(alpha)
        };

        const pointC = {
          x: element.centerX + displayLength * Math.cos(alpha + phi),
          y: element.centerY + displayLength * Math.sin(alpha + phi)
        };
        
        const arcRadius = Math.min(displayLength * 0.3, 40);
        // اتجاه سير القوس (clockwise/anticlockwise) حسب إشارة φ
        const largeArc = currentAngle > 180 ? 1 : 0;
        const sweepFlag = phi > 0 ? 1 : 0;
        
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            {/* الخط AB - أفقي دائماً متقطع */}
            <line
              x1={pointA.x}
              y1={pointA.y}
              x2={pointB.x}
              y2={pointB.y}
              stroke={isSelected ? '#ff0000' : '#000000'}
              strokeWidth={isSelected ? '3' : '2'}
              strokeDasharray="5,3"
              strokeLinecap="square"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
              fill="none"
            />
            {/* الخط AC - بزاوية للأعلى متقطع */}
            <line
              x1={pointA.x}
              y1={pointA.y}
              x2={pointC.x}
              y2={pointC.y}
              stroke={isSelected ? '#ff0000' : '#000000'}
              strokeWidth={isSelected ? '3' : '2'}
              strokeDasharray="5,3"
              strokeLinecap="square"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
              fill="none"
            />
            {/* قوس الزاوية من اتجاه AB (α) إلى AC (α+φ) */}
            <path
              d={`M ${element.centerX + arcRadius * Math.cos(alpha)} ${element.centerY + arcRadius * Math.sin(alpha)}
                  A ${arcRadius} ${arcRadius} 0 ${largeArc} ${sweepFlag}
                  ${element.centerX + arcRadius * Math.cos(alpha + phi)} ${element.centerY + arcRadius * Math.sin(alpha + phi)}`}
              stroke={isSelected ? '#ff0000' : '#f39c12'}
              strokeWidth="2"
              fill="none"
            />
            {/* نص قياس الزاوية عند منتصف القوس */}
            <text
              x={element.centerX + arcRadius * 1.2 * Math.cos(alpha + phi / 2)}
              y={element.centerY + arcRadius * 1.2 * Math.sin(alpha + phi / 2)}
              fill={isSelected ? '#ff0000' : '#2c3e50'}
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial"
            >
              {currentAngle}°
            </text>
          </g>
        );
      case 'customAngle': {
        // الزاوية المخصصة بخطوط صلبة وأطوال مختلفة
        const currentAngle = element.angle || 90;
        const angleRad = (currentAngle * Math.PI) / 180;
        
        const length1 = element.length1 || 100;
        const length2 = element.length2 || 100;
        const lineWidth1 = element.lineWidth1 || element.lineWidth || 10; // دعم للعناصر القديمة
        const lineWidth2 = element.lineWidth2 || element.lineWidth || 10; // دعم للعناصر القديمة
        
        const pointA = { x: element.centerX, y: element.centerY };
        
        const direction = element.direction || 'horizontal_right_up';
        const isRight = direction.includes('right');
        const isUp = direction.includes('up');
        const alpha = isRight ? 0 : Math.PI;
        const phi = (isRight ? (isUp ? -1 : 1) : (isUp ? 1 : -1)) * angleRad;

        const pointB = {
          x: element.centerX + length1 * Math.cos(alpha),
          y: element.centerY + length1 * Math.sin(alpha)
        };

        const pointC = {
          x: element.centerX + length2 * Math.cos(alpha + phi),
          y: element.centerY + length2 * Math.sin(alpha + phi)
        };
        
        // حساب نقاط المستطيل للخط الأول (على الجانب الخارجي)
        // الجانب الخارجي يعني عكس اتجاه الزاوية
        const perpAngle1 = alpha + Math.PI / 2; // عمودي على الخط الأول
        const rect1_p1 = { x: pointA.x, y: pointA.y };
        const rect1_p2 = { x: pointB.x, y: pointB.y };
        const rect1_p3 = { x: pointB.x + lineWidth1 * Math.cos(perpAngle1), y: pointB.y + lineWidth1 * Math.sin(perpAngle1) };
        const rect1_p4 = { x: pointA.x + lineWidth1 * Math.cos(perpAngle1), y: pointA.y + lineWidth1 * Math.sin(perpAngle1) };
        
        // حساب نقاط المستطيل للخط الثاني (على الجانب المعاكس)
        const perpAngle2 = alpha + phi - Math.PI / 2; // عمودي على الخط الثاني بالاتجاه المعاكس
        const rect2_p1 = { x: pointA.x, y: pointA.y };
        const rect2_p2 = { x: pointC.x, y: pointC.y };
        const rect2_p3 = { x: pointC.x + lineWidth2 * Math.cos(perpAngle2), y: pointC.y + lineWidth2 * Math.sin(perpAngle2) };
        const rect2_p4 = { x: pointA.x + lineWidth2 * Math.cos(perpAngle2), y: pointA.y + lineWidth2 * Math.sin(perpAngle2) };
        
        const arcRadius = Math.min(Math.min(length1, length2) * 0.3, 40);
        const largeArc = currentAngle > 180 ? 1 : 0;
        const sweepFlag = phi > 0 ? 1 : 0;
        
        return (
          <g key={element.id} onClick={(event) => handleElementClick(element, event)} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            {/* المستطيل الأول - يمثل عرض الخط الأول */}
            <polygon
              points={`${rect1_p1.x},${rect1_p1.y} ${rect1_p2.x},${rect1_p2.y} ${rect1_p3.x},${rect1_p3.y} ${rect1_p4.x},${rect1_p4.y}`}
              fill={isSelected ? 'rgba(255,0,0,0.3)' : 'rgba(52,152,219,0.3)'}
              stroke={isSelected ? '#ff0000' : (elementLayer?.color || '#000000')}
              strokeWidth="1"
            />
            
            {/* المستطيل الثاني - يمثل عرض الخط الثاني */}
            <polygon
              points={`${rect2_p1.x},${rect2_p1.y} ${rect2_p2.x},${rect2_p2.y} ${rect2_p3.x},${rect2_p3.y} ${rect2_p4.x},${rect2_p4.y}`}
              fill={isSelected ? 'rgba(255,0,0,0.3)' : 'rgba(52,152,219,0.3)'}
              stroke={isSelected ? '#ff0000' : (elementLayer?.color || '#000000')}
              strokeWidth="1"
            />
            
            {/* الخط الأول - خط صلب */}
            <line
              x1={pointA.x}
              y1={pointA.y}
              x2={pointB.x}
              y2={pointB.y}
              stroke={isSelected ? '#ff0000' : (elementLayer?.color || '#000000')}
              strokeWidth={isSelected ? '3' : '2'}
              strokeLinecap="square"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
              fill="none"
            />
            {/* الخط الثاني - خط صلب */}
            <line
              x1={pointA.x}
              y1={pointA.y}
              x2={pointC.x}
              y2={pointC.y}
              stroke={isSelected ? '#ff0000' : (elementLayer?.color || '#000000')}
              strokeWidth={isSelected ? '3' : '2'}
              strokeLinecap="square"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
              fill="none"
            />
            {/* قوس الزاوية */}
            <path
              d={`M ${element.centerX + arcRadius * Math.cos(alpha)} ${element.centerY + arcRadius * Math.sin(alpha)}
                  A ${arcRadius} ${arcRadius} 0 ${largeArc} ${sweepFlag}
                  ${element.centerX + arcRadius * Math.cos(alpha + phi)} ${element.centerY + arcRadius * Math.sin(alpha + phi)}`}
              stroke={isSelected ? '#ff0000' : '#f39c12'}
              strokeWidth="2"
              fill="none"
            />
            {/* نص قياس الزاوية */}
            <text
              x={element.centerX + arcRadius * 1.2 * Math.cos(alpha + phi / 2)}
              y={element.centerY + arcRadius * 1.2 * Math.sin(alpha + phi / 2)}
              fill={isSelected ? '#ff0000' : '#2c3e50'}
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial"
            >
              {currentAngle}°
            </text>
            {/* نص طول الخط الأول */}
            <text
              x={element.centerX + (length1 / 2) * Math.cos(alpha)}
              y={element.centerY + (length1 / 2) * Math.sin(alpha) - 8}
              fill={isSelected ? '#ff0000' : '#2c3e50'}
              fontSize="12"
              fontFamily="Arial"
              textAnchor="middle"
            >
              {Math.round(length1 * MM_PER_PIXEL)} mm
            </text>
            {/* نص طول الخط الثاني */}
            <text
              x={element.centerX + (length2 / 2) * Math.cos(alpha + phi)}
              y={element.centerY + (length2 / 2) * Math.sin(alpha + phi) - 8}
              fill={isSelected ? '#ff0000' : '#2c3e50'}
              fontSize="12"
              fontFamily="Arial"
              textAnchor="middle"
            >
              {Math.round(length2 * MM_PER_PIXEL)} mm
            </text>
            {/* نص عرض الخط على المستطيل الأول - على الضلع العمودي الآخر */}
            <text
              x={pointB.x + (lineWidth1 / 2) * Math.cos(perpAngle1)}
              y={pointB.y + (lineWidth1 / 2) * Math.sin(perpAngle1)}
              fill="#2c3e50"
              fontSize="10"
              fontWeight="bold"
              fontFamily="Arial"
              textAnchor="middle"
              transform={`rotate(${(alpha + Math.PI / 2) * 180 / Math.PI}, ${pointB.x + (lineWidth1 / 2) * Math.cos(perpAngle1)}, ${pointB.y + (lineWidth1 / 2) * Math.sin(perpAngle1)})`}
            >
              {Math.round(lineWidth1 * MM_PER_PIXEL)} mm
            </text>
            {/* نص عرض الخط على المستطيل الثاني - على الضلع العمودي الآخر */}
            <text
              x={pointC.x + (lineWidth2 / 2) * Math.cos(perpAngle2)}
              y={pointC.y + (lineWidth2 / 2) * Math.sin(perpAngle2)}
              fill="#2c3e50"
              fontSize="10"
              fontWeight="bold"
              fontFamily="Arial"
              textAnchor="middle"
              transform={`rotate(${(alpha + phi - Math.PI / 2) * 180 / Math.PI}, ${pointC.x + (lineWidth2 / 2) * Math.cos(perpAngle2)}, ${pointC.y + (lineWidth2 / 2) * Math.sin(perpAngle2)})`}
            >
              {Math.round(lineWidth2 * MM_PER_PIXEL)} mm
            </text>
            
            {/* النقاط فقط بدون تسميات */}
            {/* النقطة A - نقطة المركز */}
            <circle
              cx={pointA.x}
              cy={pointA.y}
              r="4"
              fill={isSelected ? '#ff0000' : '#e74c3c'}
            />
            
            {/* النقطة B - نهاية الخط الأول */}
            <circle
              cx={pointB.x}
              cy={pointB.y}
              r="4"
              fill={isSelected ? '#ff0000' : '#27ae60'}
            />
            
            {/* النقطة C - نهاية الخط الثاني */}
            <circle
              cx={pointC.x}
              cy={pointC.y}
              r="4"
              fill={isSelected ? '#ff0000' : '#3498db'}
            />
          </g>
        );
      }
      case 'quadrilateral':
        // حساب نقطة تقاطع الأقطار - الآن مضمونة أن تكون في المركز
        const centerX = (element.points[0].x + element.points[2].x) / 2;
        const centerY = (element.points[0].y + element.points[2].y) / 2;
        
        return (
          <g key={element.id} onClick={(event) => {
            console.log('نقر على الرباعي:', element.id);
            handleElementClick(element, event);
          }} style={{ cursor: selectedTool === 'select' ? 'pointer' : 'crosshair' }}>
            {/* رسم الأضلاع بألوان مختلفة */}
            {/* الضلع AB - أحمر */}
            <line
              x1={element.points[0].x}
              y1={element.points[0].y}
              x2={element.points[1].x}
              y2={element.points[1].y}
              stroke={isSelected ? '#ff0000' : sideColors.AB}
              strokeWidth={isSelected ? '4' : '3'}
            />
            {/* الضلع BC - أزرق */}
            <line
              x1={element.points[1].x}
              y1={element.points[1].y}
              x2={element.points[2].x}
              y2={element.points[2].y}
              stroke={isSelected ? '#ff0000' : sideColors.BC}
              strokeWidth={isSelected ? '4' : '3'}
            />
            {/* الضلع CD - أخضر */}
            <line
              x1={element.points[2].x}
              y1={element.points[2].y}
              x2={element.points[3].x}
              y2={element.points[3].y}
              stroke={isSelected ? '#ff0000' : sideColors.CD}
              strokeWidth={isSelected ? '4' : '3'}
            />
            {/* الضلع DA - برتقالي */}
            <line
              x1={element.points[3].x}
              y1={element.points[3].y}
              x2={element.points[0].x}
              y2={element.points[0].y}
              stroke={isSelected ? '#ff0000' : sideColors.DA}
              strokeWidth={isSelected ? '4' : '3'}
            />
            
            {/* رسم الأقطار */}
            <>
                <line
                  x1={element.points[0].x}
                  y1={element.points[0].y}
                  x2={element.points[2].x}
                  y2={element.points[2].y}
                  stroke={isSelected ? '#ff0000' : '#666666'}
                  strokeWidth="1"
                  strokeDasharray="3,3"
                  opacity="0.7"
                />
                <line
                  x1={element.points[1].x}
                  y1={element.points[1].y}
                  x2={element.points[3].x}
                  y2={element.points[3].y}
                  stroke={isSelected ? '#ff0000' : '#666666'}
                  strokeWidth="1" 
                  strokeDasharray="3,3"
                  opacity="0.7"
                />
                
                {/* نقطة تقاطع الأقطار */}
                <circle
                  cx={centerX}
                  cy={centerY}
                  r="3"
                  fill={isSelected ? '#ff0000' : '#666666'}
                  opacity="0.8"
                />
            </>
          </g>
        );
      default:
        return null;
    }
  };

  // وظائف الحفظ والتصدير (النسخة القديمة تمت إزالتها للحفاظ على نسخة localStorage الموحدة)

  // تحديث خصائص العنصر المحدد
  const updateElementProperty = useCallback((property, value) => {
    if (!selectedElement) return;
    
    const updatedElements = elements.map(element => {
      if (element.id === selectedElement.id) {
        const updatedElement = { ...element, [property]: parseFloat(value) || value };
        setSelectedElement(updatedElement);
        // تحديث baseWall إذا كان العنصر المحدد هو الحائط الأساسي
        if (updatedElement.type === 'baseWall') {
          setBaseWall(updatedElement);
        }
        return updatedElement;
      }
      return element;
    });
    
    setElements(updatedElements);
    saveToHistory();
  }, [selectedElement, elements, saveToHistory]);

  // حذف العنصر المحدد
  const deleteSelectedElement = useCallback(() => {
    if (!selectedElement) return;
    
    // إذا كان العنصر المحذوف رباعي، إعادة تعيين hasQuadrilateral
    if (selectedElement.type === 'quadrilateral') {
      setHasQuadrilateral(false);
    }
    
    // إذا كان العنصر المحذوف الحائط الأساسي
    if (selectedElement.type === 'baseWall') {
      setBaseWall(null);
    }
    
    const updatedElements = elements.filter(element => element.id !== selectedElement.id);
    setElements(updatedElements);
    setSelectedElement(null);
    setShowProperties(false);
    saveToHistory();
  }, [selectedElement, elements, saveToHistory]);

  // تكرار العنصر المحدد
  const duplicateSelectedElement = useCallback(() => {
    if (!selectedElement) return;

    const newElement = {
      ...selectedElement,
      id: Date.now(),
      name: selectedElement.name ? `${selectedElement.name} (نسخة)` : undefined,
      x1: selectedElement.x1 ? selectedElement.x1 + 20 : undefined,
      y1: selectedElement.y1 ? selectedElement.y1 + 20 : undefined,
      x2: selectedElement.x2 ? selectedElement.x2 + 20 : undefined,
      y2: selectedElement.y2 ? selectedElement.y2 + 20 : undefined,
      x: selectedElement.x ? selectedElement.x + 20 : undefined,
      y: selectedElement.y ? selectedElement.y + 20 : undefined,
      cx: selectedElement.cx ? selectedElement.cx + 20 : undefined,
      cy: selectedElement.cy ? selectedElement.cy + 20 : undefined,
    };
    
    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement);
    saveToHistory();
  }, [selectedElement, saveToHistory]);

  // حذف عنصر عبر معرفه (من قائمة العناصر)
  const deleteElementById = useCallback((id) => {
    setElements(prev => {
      const target = prev.find(e => e.id === id);
      if (!target) return prev;
      if (target.type === 'quadrilateral') setHasQuadrilateral(false);
      if (target.type === 'baseWall') setBaseWall(null);
      return prev.filter(e => e.id !== id);
    });
    if (selectedElement?.id === id) {
      setSelectedElement(null);
      setShowProperties(false);
    }
    saveToHistory();
  }, [selectedElement, saveToHistory]);

  // إظهار/إخفاء عنصر عبر معرفه (من قائمة العناصر)
  const toggleElementVisibility = useCallback((id) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, visible: el.visible !== false ? false : true } : el
    ));
    saveToHistory();
  }, [saveToHistory]);

  // تحديث طول الخط مع الحفاظ على نقطة البداية وتحريك النهاية
  const updateLineLength = useCallback((newLength) => {
    if (!selectedElement || selectedElement.type !== 'line') return;
    const L = parseFloat(newLength);
    if (!Number.isFinite(L) || L < 0) return;
    const dx = (selectedElement.x2 ?? 0) - (selectedElement.x1 ?? 0);
    const dy = (selectedElement.y2 ?? 0) - (selectedElement.y1 ?? 0);
    const angle = Math.atan2(dy, dx);
    const nx2 = (selectedElement.x1 ?? 0) + L * Math.cos(angle);
    const ny2 = (selectedElement.y1 ?? 0) + L * Math.sin(angle);
    setElements(prev => prev.map(el => el.id === selectedElement.id ? { ...el, x2: nx2, y2: ny2 } : el));
    setSelectedElement(prev => prev ? { ...prev, x2: nx2, y2: ny2 } : prev);
    saveToHistory();
  }, [selectedElement, setElements, setSelectedElement, saveToHistory]);

  // تحديث زاوية الخط بالدرجات مع الحفاظ على طوله ونقطة البداية
  const updateLineAngle = useCallback((newAngleDeg) => {
    if (!selectedElement || selectedElement.type !== 'line') return;
    const deg = parseFloat(newAngleDeg);
    if (!Number.isFinite(deg)) return;
    const rad = (deg * Math.PI) / 180;
    const dx = (selectedElement.x2 ?? 0) - (selectedElement.x1 ?? 0);
    const dy = (selectedElement.y2 ?? 0) - (selectedElement.y1 ?? 0);
    const L = Math.sqrt(dx * dx + dy * dy);
    const nx2 = (selectedElement.x1 ?? 0) + L * Math.cos(rad);
    const ny2 = (selectedElement.y1 ?? 0) + L * Math.sin(rad);
    setElements(prev => prev.map(el => el.id === selectedElement.id ? { ...el, x2: nx2, y2: ny2 } : el));
    setSelectedElement(prev => prev ? { ...prev, x2: nx2, y2: ny2 } : prev);
    saveToHistory();
  }, [selectedElement, setElements, setSelectedElement, saveToHistory]);

  // تحديث قيم عنصر الزاوية (الطول والدرجة)
  const updateAngleValue = useCallback((newAngleDeg) => {
    if (!selectedElement || selectedElement.type !== 'angle') return;
    const deg = parseFloat(newAngleDeg);
    if (!Number.isFinite(deg)) return;
    setElements(prev => prev.map(el => el.id === selectedElement.id ? { ...el, angle: deg } : el));
    setSelectedElement(prev => prev ? { ...prev, angle: deg } : prev);
    saveToHistory();
  }, [selectedElement, setElements, setSelectedElement, saveToHistory]);

  const updateAngleLength = useCallback((newLen) => {
    if (!selectedElement || selectedElement.type !== 'angle') return;
    const L = parseFloat(newLen);
    if (!Number.isFinite(L) || L < 0) return;
    setElements(prev => prev.map(el => el.id === selectedElement.id ? { ...el, length: L } : el));
    setSelectedElement(prev => prev ? { ...prev, length: L } : prev);
    saveToHistory();
  }, [selectedElement, setElements, setSelectedElement, saveToHistory]);

  // تحديث مسافة القياس dimension بتحريك نقطة النهاية على نفس الاتجاه
  const updateDimensionDistance = useCallback((newDist) => {
    if (!selectedElement || selectedElement.type !== 'dimension') return;
    const D = parseFloat(newDist) / MM_PER_PIXEL; // تحويل من mm إلى pixels
    if (!Number.isFinite(D) || D < 0) return;
    const dx = (selectedElement.x2 ?? 0) - (selectedElement.x1 ?? 0);
    const dy = (selectedElement.y2 ?? 0) - (selectedElement.y1 ?? 0);
    const angle = Math.atan2(dy, dx);
    const nx2 = (selectedElement.x1 ?? 0) + D * Math.cos(angle);
    const ny2 = (selectedElement.y1 ?? 0) + D * Math.sin(angle);
    setElements(prev => prev.map(el => el.id === selectedElement.id ? { ...el, x2: nx2, y2: ny2, distance: D.toFixed(1) } : el));
    setSelectedElement(prev => prev ? { ...prev, x2: nx2, y2: ny2, distance: D.toFixed(1) } : prev);
    saveToHistory();
  }, [selectedElement, setElements, setSelectedElement, saveToHistory]);

  // مسح الكل (النسخة القديمة تمت إزالتها للحفاظ على نسخة موحدة)

  // تأكيد إدخال قياس الزاوية
  const confirmAngle = useCallback(() => {
    if (!pendingAngleId) return;
    const angle = parseFloat(angleValue) || 90;
    setElements(prev => prev.map(el => el.id === pendingAngleId ? { ...el, angle } : el));
    setPendingAngleId(null);
    setShowAngleModal(false);
    saveToHistory();
  }, [pendingAngleId, angleValue, saveToHistory]);

  // إلغاء إدخال الزاوية
  const cancelAngle = useCallback(() => {
    if (pendingAngleId) {
      setElements(prev => prev.filter(el => el.id !== pendingAngleId));
    }
    setPendingAngleId(null);
    setShowAngleModal(false);
  }, [pendingAngleId]);

  // تأكيد/إلغاء إدخال النص
  const confirmText = useCallback(() => {
    if (!pendingTextId) return;
    const txt = (textValue || '').trim() || 'نص';
    const size = parseInt(textSizeValue, 10) || 16;
    setElements(prev => prev.map(el => el.id === pendingTextId ? { ...el, text: txt, fontSize: size } : el));
    setSelectedElement(prev => prev && prev.id === pendingTextId ? { ...prev, text: txt, fontSize: size } : prev);
    setPendingTextId(null);
    setShowTextModal(false);
    saveToHistory();
  }, [pendingTextId, textValue, textSizeValue, saveToHistory]);

  const cancelText = useCallback(() => {
    if (pendingTextId) {
      setElements(prev => prev.filter(el => el.id !== pendingTextId));
    }
    setPendingTextId(null);
    setShowTextModal(false);
  }, [pendingTextId]);

  // تأكيد إدخال بيانات المكون
  const confirmComponentInput = useCallback(() => {
    if (!baseWall) return;
    
    // تحويل القيم من mm إلى pixels حسب المقياس (القسمة على MM_PER_PIXEL)
    const width = parseFloat(componentInputData.width) / MM_PER_PIXEL;
    const height = parseFloat(componentInputData.height) / MM_PER_PIXEL;
    const distanceFromBottom = parseFloat(componentInputData.distanceFromBottom) / MM_PER_PIXEL;
    const distanceFromLeft = parseFloat(componentInputData.distanceFromLeft) / MM_PER_PIXEL;
    
    if (isNaN(width) || isNaN(height) || isNaN(distanceFromBottom) || isNaN(distanceFromLeft)) {
      alert(t.enterAllFields);
      return;
    }
    
    if (width <= 0 || height <= 0) {
      alert(t.mustBeGreaterThanZero);
      return;
    }
    
    // حساب موضع المكون بناءً على الحائط الأساسي
    const wallX = baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x;
    const wallY = baseWall.height < 0 ? baseWall.y + baseWall.height : baseWall.y;
    const wallW = Math.abs(baseWall.width);
    const wallH = Math.abs(baseWall.height);
    
    // الموضع: من اليسار + المسافة من اليسار، من الأسفل - الارتفاع - المسافة من الأسفل
    const componentX = wallX + distanceFromLeft;
    const componentY = wallY + wallH - distanceFromBottom - height;
    
    // التحقق من أن المكون داخل حدود الحائط الأساسي (عدا الباب الأرضي)
    if (selectedComponentLabel !== 'floorDoor' && !isPointInsideBaseWall(componentX, componentY, width, height)) {
      alert(t.outsideBaseWall);
      return;
    }
    
    // إنشاء الرمز مع الرقم التسلسلي
    const symbolWithNumber = generateComponentSymbol(selectedComponentLabel);
    
    const newComponent = {
      type: 'component',
      id: Date.now(),
      name: t[selectedComponentLabel] || selectedComponentLabel,
      x: componentX,
      y: componentY,
      width: width,
      height: height,
      fill: getComponentColor(selectedComponentLabel),
      label: symbolWithNumber, // استخدام الرمز الجديد بدلاً من الاسم الكامل
      originalType: selectedComponentLabel, // حفظ النوع الأصلي للمرجع
      direction: selectedComponentLabel === 'floorDoor' ? selectedFloorDoorDirection : undefined, // إضافة الاتجاه للباب الأرضي
      layer: 'layer1',
      visible: true
    };
    
    setElements(prev => [...prev, newComponent]);
    setShowComponentModal(false);
    setComponentInputData({
      width: '',
      height: '',
      distanceFromBottom: '',
      distanceFromLeft: ''
    });
    saveToHistory();
  }, [baseWall, componentInputData, selectedComponentLabel, getComponentColor, saveToHistory]);
  
  // إلغاء إدخال بيانات المكون
  const cancelComponentInput = useCallback(() => {
    setShowComponentModal(false);
    setComponentInputData({
      width: '',
      height: '',
      distanceFromBottom: '',
      distanceFromLeft: ''
    });
  }, []);

  // دوال إدارة الأجهزة
  const addDevice = useCallback(() => {
    if (!deviceForm.name || !deviceForm.model) {
      alert('يرجى إدخال الاسم والموديل على الأقل');
      return;
    }
    
    const newDevice = {
      id: Date.now(),
      name: deviceForm.name,
      model: deviceForm.model,
      w: deviceForm.w,
      h: deviceForm.h,
      d: deviceForm.d,
      notes: deviceForm.notes
    };
    
    setDevices(prev => [...prev, newDevice]);
    setDeviceForm({
      id: '',
      name: '',
      model: '',
      w: '',
      h: '',
      d: '',
      notes: ''
    });
  }, [deviceForm]);

  const updateDevice = useCallback(() => {
    if (!editingDeviceId) return;
    
    setDevices(prev => prev.map(device => 
      device.id === editingDeviceId 
        ? { ...device, ...deviceForm, id: device.id }
        : device
    ));
    
    setEditingDeviceId(null);
    setDeviceForm({
      id: '',
      name: '',
      model: '',
      w: '',
      h: '',
      d: '',
      notes: ''
    });
  }, [editingDeviceId, deviceForm]);

  const deleteDevice = useCallback((deviceId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الجهاز؟')) {
      setDevices(prev => prev.filter(device => device.id !== deviceId));
      if (editingDeviceId === deviceId) {
        setEditingDeviceId(null);
        setDeviceForm({
          id: '',
          name: '',
          model: '',
          w: '',
          h: '',
          d: '',
          notes: ''
        });
      }
    }
  }, [editingDeviceId]);

  const selectDeviceForEdit = useCallback((device) => {
    setEditingDeviceId(device.id);
    setDeviceForm({
      id: device.id,
      name: device.name,
      model: device.model,
      w: device.w,
      h: device.h,
      d: device.d,
      notes: device.notes
    });
  }, []);

  if (!hasActiveProject) {
    return (
      <div className="cad-workspace">
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.97)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          color: '#222',
        }}>
          <div style={{ marginBottom: 24 }}>🚫 لا يمكنك استخدام صفحة الرسم بدون مشروع نشط</div>
          <div style={{ fontSize: '1.2rem', color: '#888' }}>يرجى إنشاء أو تفعيل مشروع أولاً من صفحة إدارة المشاريع</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cad-workspace">
      <div className={`toolbar ${showToolbar ? '' : 'hidden'}`} style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
        <button
          className="drawer-toggle"
          onClick={() => setShowToolbar(prev => !prev)}
          title={showToolbar ? 'إخفاء الشريط' : 'إظهار الشريط'}
          aria-label={showToolbar ? 'إخفاء الشريط' : 'إظهار الشريط'}
        >
          {showToolbar ? (language === 'ar' ? '❮' : '❯') : '☰'}
        </button>
        <div className="toolbar-section section-tools order-1">
          <h3>
            {t.drawingTools}
            <button className="group-toggle" onClick={() => toggleGroup('tools')}>{collapsed.tools ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.tools && (
          <div className="drawing-tools">

            <button
              className={`draw-tool-item ${selectedTool === 'baseWall' ? 'active' : ''}`}
              onClick={() => setSelectedTool('baseWall')}
              title={t.baseWall}>
              <span className="draw-tool-icon">⬛</span>
              <span className="draw-tool-label">{singleLabel('baseWall')}</span>
            </button>



            <button
              className={`draw-tool-item ${selectedTool === 'line' ? 'active' : ''}`}
              onClick={() => setSelectedTool('line')}
              title={t.line}>
              <span className="draw-tool-icon">📏</span>
              <span className="draw-tool-label">{singleLabel('line')}</span>
            </button>

            <button
              className={`draw-tool-item ${selectedTool === 'rectangle' ? 'active' : ''}`}
              onClick={() => setSelectedTool('rectangle')}
              title={t.rectangle}>
              <span className="draw-tool-icon">⬜</span>
              <span className="draw-tool-label">{singleLabel('rectangle')}</span>
            </button>

            <button
              className={`draw-tool-item ${selectedTool === 'circle' ? 'active' : ''}`}
              onClick={() => setSelectedTool('circle')}
              title={t.circle}>
              <span className="draw-tool-icon">⭕</span>
              <span className="draw-tool-label">{singleLabel('circle')}</span>
            </button>

            <button
              className={`draw-tool-item ${selectedTool === 'angle' ? 'active' : ''}`}
              onClick={() => setSelectedTool('angle')}
              title={t.angle}>
              <span className="draw-tool-icon">∠</span>
              <span className="draw-tool-label">{singleLabel('angle')}</span>
            </button>

            <button
              className={`draw-tool-item ${selectedTool === 'dimension' ? 'active' : ''}`}
              onClick={() => setSelectedTool('dimension')}
              title={t.dimension}>
              <span className="draw-tool-icon">📐</span>
              <span className="draw-tool-label">{singleLabel('dimension')}</span>
            </button>

            <button
              className={`draw-tool-item ${selectedTool === 'text' ? 'active' : ''}`}
              onClick={() => setSelectedTool('text')}
              title={t.text}>
              <span className="draw-tool-icon">T</span>
              <span className="draw-tool-label">{singleLabel('text')}</span>
            </button>


          </div>
          )}
        </div>

        {/* الأدوات الأساسية - تصميم مبسط */}
        <div className="toolbar-section section-basic">
          <h3>
            {t.basicTools}
            <button className="group-toggle" onClick={() => toggleGroup('basic')}>{collapsed.basic ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.basic && (
          <div className="basic-tools">
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'wallDoor' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('wallDoor'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🚪</span>
              <span className="basic-tool-label">{singleLabel('wallDoor')}</span>
            </button>
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'floorDoor' ? 'active' : ''}`}
              onClick={() => { 
                if(baseWall) { 
                  setShowFloorDoorDirectionModal(true);
                } else { 
                  alert(t.mustDrawBaseWall); 
                } 
              }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🚪</span>
              <span className="basic-tool-label">{singleLabel('floorDoor')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'wallWindow' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('wallWindow'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🪟</span>
              <span className="basic-tool-label">{singleLabel('wallWindow')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'electricity' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('electricity'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">⚡</span>
              <span className="basic-tool-label">{singleLabel('electricity')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'water' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('water'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">💧</span>
              <span className="basic-tool-label">{singleLabel('water')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'gas' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('gas'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🔥</span>
              <span className="basic-tool-label">{singleLabel('gas')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'duct' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('duct'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">📍</span>
              <span className="basic-tool-label">{singleLabel('duct')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'bidetSprayer' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('bidetSprayer'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🚿</span>
              <span className="basic-tool-label">{singleLabel('bidetSprayer')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'maintenance' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('maintenance'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">🔧</span>
              <span className="basic-tool-label">{singleLabel('maintenance')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'airConditioner' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('airConditioner'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">❄️</span>
              <span className="basic-tool-label">{singleLabel('airConditioner')}</span>
            </button>
            
            <button 
              className={`basic-tool-item ${selectedTool === 'component' && selectedComponentLabel === 'wallDuct' ? 'active' : ''}`}
              onClick={() => { if(baseWall) { setSelectedTool('component'); setSelectedComponentLabel('wallDuct'); } else { alert(t.mustDrawBaseWall); } }}
              disabled={!baseWall}>
              <span className="basic-tool-icon">📌</span>
              <span className="basic-tool-label">{singleLabel('wallDuct')}</span>
            </button>
          </div>
          )}
        </div>

        

        {/* قسم اختيار الزاوية - يظهر فقط عند اختيار أداة الزاوية */}
        {selectedTool === 'angle' && (
          <div className="toolbar-section angle-selector">
            <h3>
              إعداد الزاوية
              <button className="group-toggle" onClick={() => toggleGroup('angle')}>{collapsed.angle ? '▶' : '▼'}</button>
            </h3>
            {!collapsed.angle && (
            <div className="angle-config">
              <div className="angle-direction-selector">
                <label>نوع الزاوية:</label>
                <select 
                  value={selectedAngleDirection} 
                  onChange={(e) => setSelectedAngleDirection(e.target.value)}
                  className="layer-select"
                >
                  <option value="horizontal_left_up">أفقي يسار - أعلى</option>
                  <option value="horizontal_right_up">أفقي يمين - أعلى</option>
                  <option value="horizontal_left_down">أفقي يسار - أسفل</option>
                  <option value="horizontal_right_down">أفقي يمين - أسفل</option>
                </select>
              </div>
              <div className="angle-preview-container">
                <div className="angle-mini-preview">
                  <svg width="100" height="60" viewBox="0 0 100 60">
                    {/* خط أفقي AB */}
                    <line 
                      x1={selectedAngleDirection.includes('right') ? "30" : "70"} 
                      y1="30" 
                      x2={selectedAngleDirection.includes('right') ? "70" : "30"} 
                      y2="30" 
                      stroke="#3498db" 
                      strokeWidth="2" 
                      strokeDasharray="3,2" 
                    />
                    {/* خط الزاوية AC */}
                    <line 
                      x1="50" 
                      y1="30" 
                      x2={selectedAngleDirection.includes('right') ? "65" : "35"} 
                      y2={selectedAngleDirection.includes('down') ? "45" : "15"} 
                      stroke="#3498db" 
                      strokeWidth="2" 
                      strokeDasharray="3,2" 
                    />
                    {/* قوس الزاوية */}
                    <path
                      d={selectedAngleDirection.includes('right') && selectedAngleDirection.includes('up') ? "M 55 30 A 5 5 0 0 0 54.35 25.35" :
                         selectedAngleDirection.includes('left') && selectedAngleDirection.includes('up') ? "M 45 30 A 5 5 0 0 1 45.65 25.35" :
                         selectedAngleDirection.includes('right') && selectedAngleDirection.includes('down') ? "M 55 30 A 5 5 0 0 1 54.35 34.65" :
                         "M 45 30 A 5 5 0 0 0 45.65 34.65"}
                      stroke="#f39c12" 
                      strokeWidth="1.5" 
                      fill="none" 
                    />
                    {/* نقطة المركز */}
                    <circle cx="50" cy="30" r="2" fill="#e74c3c" />
                    {/* تسميات */}
                    <text x="45" y="45" fontSize="8" fill="#333">A</text>
                    <text x={selectedAngleDirection.includes('right') ? "68" : "25"} y="35" fontSize="8" fill="#333">B</text>
                    <text x={selectedAngleDirection.includes('right') ? "63" : "30"} y={selectedAngleDirection.includes('down') ? "50" : "10"} fontSize="8" fill="#333">C</text>
                  </svg>
                </div>
              </div>
              <div className="angle-info">
                <p>اختر نوع الزاوية من القائمة أعلاه، ثم ارسم على اللوحة</p>
              </div>
            </div>
            )}
          </div>
        )}
        
        

        <div className="toolbar-section section-layers">
          <h3>
            {t.layers || 'الطبقات'}
            <button className="group-toggle" onClick={() => toggleGroup('layers')}>{collapsed.layers ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.layers && (
          <div className="layers-panel">
            {Object.entries(layers).map(([key, layer]) => (
              <div key={key} className="layer-item">
                <input
                  type="checkbox"
                  checked={layer.visible}
                  onChange={(e) => setLayers(prev => ({
                    ...prev,
                    [key]: { ...prev[key], visible: e.target.checked }
                  }))}
                />
                <span className="layer-color" style={{ backgroundColor: layer.color }}></span>
                <span className="layer-name">{
                  key === 'layer1' ? singleLabel('baseLayer') :
                  key === 'layer2' ? singleLabel('linesLayer') :
                  key === 'layer3' ? singleLabel('textLayer') :
                  key === 'measurements' ? singleLabel('measurementsLayer') :
                  key
                }</span>
              </div>
            ))}
          </div>
          )}
        </div>

        <div className="toolbar-section section-settings">
          <h3>
            {t.settings || 'الإعدادات'}
            <button className="group-toggle" onClick={() => toggleGroup('settings')}>{collapsed.settings ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.settings && (
          <div className="settings">
            <label className="setting-item">
              <input
                type="checkbox"
                checked={snapToGrid}
                onChange={(e) => setSnapToGrid(e.target.checked)}
              />
              {t.snapToGrid || 'الالتصاق بالشبكة'}
            </label>
            <div className="setting-item">
              <label>{t.gridSize || 'حجم الشبكة'} (px):</label>
              <input
                type="number"
                min="1"
                value={gridSize}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setGridSize(Number.isFinite(v) && v > 0 ? v : 1);
                }}
                className="property-input"
                style={{ maxWidth: 120 }}
              />
            </div>
            <div className="zoom-info">{t.zoom || 'التكبير'}: {(zoom * 100).toFixed(0)}%</div>
          </div>
          )}
        </div>
        
        
        
        <div className="toolbar-section section-coords">
          <h3>
            {t.coordinates || 'الإحداثيات'}
            <button className="group-toggle" onClick={() => toggleGroup('coords')}>{collapsed.coords ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.coords && (
          <div className="coordinates">
            X: {mousePosition.x}, Y: {mousePosition.y}
          </div>
          )}
        </div>

        {/* قائمة العناصر - تصميم مبسط */}
        <div className="toolbar-section section-elements">
          <h3>
            {t.elementsList || 'العناصر'} ({elements.length})
            <button className="group-toggle" onClick={() => toggleGroup('elements')}>{collapsed.elements ? '▶' : '▼'}</button>
          </h3>
          {!collapsed.elements && (
          <div className="elements-list">
            {elements.length === 0 ? (
              <div className="empty-message">{t.noElements || 'لا توجد عناصر'}</div>
            ) : (
              elements.map((el, idx) => (
                <div
                  key={el.id}
                  className={`element-row ${selectedElement?.id === el.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedElement(el); setShowProperties(true); }}>
                  

                  <span className="element-num">{idx + 1}</span>
                  <span className="element-type">
                    {(() => {
                      // Show el.name only if it matches the current language
                      if (el.name) {
                        // Simple heuristic: if language is 'ar', show if contains Arabic letters; if 'en', show if contains only English letters/numbers
                        const isArabic = /[\u0600-\u06FF]/.test(el.name);
                        if ((language === 'ar' && isArabic) || (language === 'en' && !isArabic)) {
                          return el.name;
                        }
                      }
                      // For components, show translated type + symbol (e.g., باب e1)
                      if (el.type === 'component') {
                        const compKey = el.componentType || 'component';
                        const name = singleLabel(compKey);
                        return `${name} ${el.typeId || ''}`.trim();
                      }
                      // For other types, show translated type
                      if (el.type && tAr[el.type] && tEn[el.type]) {
                        return singleLabel(el.type);
                      }
                      // Fallback: just show type
                      return el.type;
                    })()}
                  </span>
                  
                  <div className="element-btns">
                    <button
                      className="btn-toggle"
                      onClick={(e) => { e.stopPropagation(); toggleElementVisibility(el.id); }}
                      title={el.visible !== false ? (t.hide || 'إخفاء') : (t.show || 'إظهار')}>
                      {el.visible !== false ? '👁️' : '�'}
                    </button>
                    <button
                      className="btn-delete"
                      onClick={(e) => { e.stopPropagation(); deleteElementById(el.id); }}
                      title={t.delete || 'حذف'}>
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          )}
        </div>

        

        {/* لوحة الخصائص */}
        {showProperties && selectedElement && (
          <div className="toolbar-section properties-panel">
            <h3>{t.elementProperties}</h3>
            <div className="properties-content">
            <div className="property-row">
              <label>{t.dragToMove || 'تحريك بالماوس'}:</label>
              <div
                className="move-handle"
                onMouseDown={startDragSelectedFromPanel}
                title={t.dragToMove || 'اسحب هنا لتحريك العنصر المختار'}
              >
                ⛶ {t.dragToMove || 'اسحب هنا لتحريك العنصر'}
              </div>
            </div>
              <div className="property-row">
                <label>{t.type || 'النوع'}:</label>
                <span className="property-value">{
                  selectedElement.type === 'line' ? t.line :
                  selectedElement.type === 'rectangle' ? t.rectangle :
                  selectedElement.type === 'circle' ? t.circle :
                  selectedElement.type === 'dimension' ? t.dimension :
                  selectedElement.type === 'baseWall' ? t.baseWall :
                  selectedElement.type === 'component' ? (t.component || 'مكون') :
                  selectedElement.type === 'angle' ? t.angle :
                  selectedElement.type === 'customAngle' ? t.customAngle :
                  selectedElement.type === 'text' ? t.text :
                  selectedElement.type === 'quadrilateral' ? t.quadrilateral : selectedElement.type
                }</span>
              </div>


              
              <div className="property-row">
                <label>{t.layer || 'الطبقة'}:</label>
                <select
                  value={selectedElement.layer}
                  onChange={(e) => updateElementProperty('layer', e.target.value)}
                  className="property-input"
                >
                  {['layer1', 'layer2', 'layer3', 'measurements'].map((key) => (
                    <option key={key} value={key}>{
                      key === 'layer1' ? singleLabel('baseLayer') :
                      key === 'layer2' ? singleLabel('linesLayer') :
                      key === 'layer3' ? singleLabel('textLayer') :
                      key === 'measurements' ? singleLabel('measurementsLayer') :
                      key
                    }</option>
                  ))}
                </select>
                <span style={{ color: layers[selectedElement.layer]?.color, marginLeft: 8 }}>●</span>
              </div>

              {/* خصائص الخط */}
              {selectedElement.type === 'line' && (
                <>
                  <div className="property-row">
                    <label>{t.startPointX}:</label>
                    <input
                      type="number"
                      value={selectedElement.x1}
                      onChange={(e) => updateElementProperty('x1', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.startPointY}:</label>
                    <input
                      type="number"
                      value={selectedElement.y1}
                      onChange={(e) => updateElementProperty('y1', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.endPointX}:</label>
                    <input
                      type="number"
                      value={selectedElement.x2}
                      onChange={(e) => updateElementProperty('x2', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.endPointY}:</label>
                    <input
                      type="number"
                      value={selectedElement.y2}
                      onChange={(e) => updateElementProperty('y2', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.length} (mm):</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={Math.round(
                        Math.sqrt(
                          Math.pow((selectedElement.x2 ?? 0) - (selectedElement.x1 ?? 0), 2) +
                          Math.pow((selectedElement.y2 ?? 0) - (selectedElement.y1 ?? 0), 2)
                        ) * 10
                      )}
                      onChange={(e) => updateLineLength(parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.angleDegrees}:</label>
                    <input
                      type="number"
                      step="0.1"
                      value={(
                        (Math.atan2(
                          (selectedElement.y2 ?? 0) - (selectedElement.y1 ?? 0),
                          (selectedElement.x2 ?? 0) - (selectedElement.x1 ?? 0)
                        ) * 180) / Math.PI
                      ).toFixed(1)}
                      onChange={(e) => updateLineAngle(e.target.value)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص المستطيل */}
              {selectedElement.type === 'rectangle' && (
                <>
                  <div className="property-row">
                    <label>{t.distanceFromLeft || 'المسافة من الضلع الأيسر'} (مم):</label>
                    <input
                      type="number"
                      value={baseWall ? Math.round((selectedElement.x - (baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x)) * MM_PER_PIXEL) : Math.round(selectedElement.x)}
                      onChange={(e) => {
                        if (baseWall) {
                          const wallX = baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x;
                          const newX = wallX + parseFloat(e.target.value) / MM_PER_PIXEL;
                          updateElementProperty('x', newX);
                        } else {
                          updateElementProperty('x', e.target.value);
                        }
                      }}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.distanceFromBottom || 'المسافة من القاعدة'} (مم):</label>
                    <input
                      type="number"
                      value={baseWall ? Math.round((baseWall.y + baseWall.height - (selectedElement.y + selectedElement.height)) * MM_PER_PIXEL) : Math.round(selectedElement.y)}
                      onChange={(e) => {
                        if (baseWall) {
                          const newY = baseWall.y + baseWall.height - selectedElement.height - parseFloat(e.target.value) / MM_PER_PIXEL;
                          updateElementProperty('y', newY);
                        } else {
                          updateElementProperty('y', e.target.value);
                        }
                      }}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.width} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.width) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('width', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.height} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.height) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('height', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص الحائط الأساسي */}
              {selectedElement.type === 'baseWall' && (
                <>
                  <div className="property-row">
                    <label>{t.positionX}:</label>
                    <input
                      type="number"
                      value={selectedElement.x}
                      onChange={(e) => updateElementProperty('x', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.positionY}:</label>
                    <input
                      type="number"
                      value={selectedElement.y}
                      onChange={(e) => updateElementProperty('y', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.width} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.width) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('width', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.height} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.height) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('height', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص المكون */}
              {selectedElement.type === 'component' && (
                <>
                  <div className="property-row">
                    <label>{t.distanceFromLeft || 'المسافة من الضلع الأيسر'} (مم):</label>
                    <input
                      type="number"
                      value={baseWall ? Math.round((selectedElement.x - (baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x)) * MM_PER_PIXEL) : Math.round(selectedElement.x)}
                      onChange={(e) => {
                        if (baseWall) {
                          const wallX = baseWall.width < 0 ? baseWall.x + baseWall.width : baseWall.x;
                          const newX = wallX + parseFloat(e.target.value) / MM_PER_PIXEL;
                          updateElementProperty('x', newX);
                        } else {
                          updateElementProperty('x', e.target.value);
                        }
                      }}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.distanceFromBottom || 'المسافة من القاعدة'} (مم):</label>
                    <input
                      type="number"
                      value={baseWall ? Math.round((baseWall.y + baseWall.height - (selectedElement.y + selectedElement.height)) * MM_PER_PIXEL) : Math.round(selectedElement.y)}
                      onChange={(e) => {
                        if (baseWall) {
                          const newY = baseWall.y + baseWall.height - selectedElement.height - parseFloat(e.target.value) / MM_PER_PIXEL;
                          updateElementProperty('y', newY);
                        } else {
                          updateElementProperty('y', e.target.value);
                        }
                      }}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.width} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.width) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('width', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.height} (mm):</label>
                    <input
                      type="number"
                      value={Math.round(Math.abs(selectedElement.height) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('height', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>{t.color}:</label>
                    <select
                      value={selectedElement.fill || '#000000'}
                      onChange={(e) => updateElementProperty('fill', e.target.value)}
                      className="property-input"
                    >
                      <option value="#000000">{t.black}</option>
                      <option value="#ff0000">{t.red}</option>
                      <option value="#4caf50">{t.green}</option>
                      <option value="#f1c40f">{t.yellow}</option>
                    </select>
                  </div>
                </>
              )}

              {/* خصائص الدائرة */}
              {selectedElement.type === 'circle' && (
                <>
                  <div className="property-row">
                    <label>المركز X:</label>
                    <input
                      type="number"
                      value={selectedElement.cx}
                      onChange={(e) => updateElementProperty('cx', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>المركز Y:</label>
                    <input
                      type="number"
                      value={selectedElement.cy}
                      onChange={(e) => updateElementProperty('cy', e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>نصف القطر:</label>
                    <input
                      type="number"
                      value={Math.abs(selectedElement.r)}
                      onChange={(e) => updateElementProperty('r', e.target.value)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص القياس */}
              {selectedElement.type === 'dimension' && (
                <>
                  <div className="property-row">
                    <label>المسافة:</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={Math.round(selectedElement.distance * MM_PER_PIXEL)}
                      onChange={(e) => updateDimensionDistance(e.target.value)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص الزاوية */}
              {selectedElement.type === 'angle' && (
                <>
                  <div className="property-row">
                    <label>قياس الزاوية (°):</label>
                    <input
                      type="number"
                      step="0.1"
                      value={selectedElement.angle || 90}
                      onChange={(e) => updateAngleValue(e.target.value)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>الطول:</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={selectedElement.length || 100}
                      onChange={(e) => updateAngleLength(e.target.value)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص الحائط المنفرد */}
              {selectedElement.type === 'customAngle' && (
                <>
                  <div className="property-row">
                    <label>قياس الزاوية (°):</label>
                    <input
                      type="number"
                      step="1"
                      value={selectedElement.angle || 90}
                      onChange={(e) => updateElementProperty('angle', parseFloat(e.target.value))}
                      className="property-input"
                      min="0"
                      max="360"
                    />
                  </div>
                  <div className="property-row">
                    <label>اتجاه الزاوية:</label>
                    <select
                      value={selectedElement.direction || 'horizontal_right_up'}
                      onChange={(e) => updateElementProperty('direction', e.target.value)}
                      className="property-input"
                    >
                      <option value="horizontal_right_up">أفقي يمين - أعلى ↗</option>
                      <option value="horizontal_right_down">أفقي يمين - أسفل ↘</option>
                      <option value="horizontal_left_up">أفقي يسار - أعلى ↖</option>
                      <option value="horizontal_left_down">أفقي يسار - أسفل ↙</option>
                    </select>
                  </div>
                  <div className="property-row" style={{ borderTop: '1px solid #ddd', paddingTop: '10px', marginTop: '10px' }}>
                    <label style={{ fontWeight: 'bold', color: '#27ae60' }}>الخط الأول:</label>
                  </div>
                  <div className="property-row">
                    <label>الاسم:</label>
                    <input
                      type="text"
                      value={selectedElement.name1 || 'AB'}
                      onChange={(e) => updateElementProperty('name1', e.target.value)}
                      className="property-input"
                      placeholder="AB"
                    />
                  </div>
                  <div className="property-row">
                    <label>الطول (mm):</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={Math.round((selectedElement.length1 || 100) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('length1', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>السُمك (mm):</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={((selectedElement.lineWidth1 || 1) * MM_PER_PIXEL).toFixed(1)}
                      onChange={(e) => updateElementProperty('lineWidth1', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row" style={{ borderTop: '1px solid #ddd', paddingTop: '10px', marginTop: '10px' }}>
                    <label style={{ fontWeight: 'bold', color: '#3498db' }}>الخط الثاني:</label>
                  </div>
                  <div className="property-row">
                    <label>الاسم:</label>
                    <input
                      type="text"
                      value={selectedElement.name2 || 'AC'}
                      onChange={(e) => updateElementProperty('name2', e.target.value)}
                      className="property-input"
                      placeholder="AC"
                    />
                  </div>
                  <div className="property-row">
                    <label>الطول (mm):</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={Math.round((selectedElement.length2 || 100) * MM_PER_PIXEL)}
                      onChange={(e) => updateElementProperty('length2', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                  <div className="property-row">
                    <label>السُمك (mm):</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={((selectedElement.lineWidth2 || 1) * MM_PER_PIXEL).toFixed(1)}
                      onChange={(e) => updateElementProperty('lineWidth2', parseFloat(e.target.value) / MM_PER_PIXEL)}
                      className="property-input"
                    />
                  </div>
                </>
              )}

              {/* خصائص الرباعي */}
              {selectedElement.type === 'quadrilateral' && (
                <>
                  <div className="property-row">
                    <label>الأضلاع الملونة:</label>
                    <div className="quadrilateral-info colored-sides">
                      <span style={{backgroundColor: sideColors.AB}}>AB: {selectedElement.sides.a}</span>
                      <span style={{backgroundColor: sideColors.BC}}>BC: {selectedElement.sides.b}</span>
                      <span style={{backgroundColor: sideColors.CD}}>CD: {selectedElement.sides.c}</span>
                      <span style={{backgroundColor: sideColors.DA}}>DA: {selectedElement.sides.d}</span>
                    </div>
                  </div>
                  <div className="property-row">
                    <label>الأقطار:</label>
                    <div className="quadrilateral-info">
                      <span>القطر AC: {selectedElement.diagonals.diagonal1}</span>
                      <span>القطر BD: {selectedElement.diagonals.diagonal2}</span>
                    </div>
                  </div>
                  <div className="property-row">
                    <label>النقاط:</label>
                    <div className="quadrilateral-points">
                      {selectedElement.points.map((point, index) => {
                        const labels = ['A', 'B', 'C', 'D'];
                        return (
                          <span key={index}>
                            {labels[index]}: ({point.x.toFixed(1)}, {point.y.toFixed(1)})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="property-row">
                    <label>نقطة تقاطع الأقطار:</label>
                    <div className="center-point">
                      <span>
                        المركز: ({((selectedElement.points[0].x + selectedElement.points[2].x) / 2).toFixed(1)}, {((selectedElement.points[0].y + selectedElement.points[2].y) / 2).toFixed(1)})
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* أزرار العمليات */}
              <div className="property-actions">
                <button className="property-btn duplicate-btn" onClick={duplicateSelectedElement}>
                  📋 {t.duplicate || 'نسخ'}
                </button>
                <button className="property-btn delete-btn" onClick={deleteSelectedElement}>
                  🗑️ {t.delete}
                </button>
                <button className="property-btn close-btn" onClick={() => setShowProperties(false)}>
                  ✖️ {t.close}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* نافذة إدخال بيانات الرباعي */}
      {showQuadrilateralModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>🔷 {t.enterQuadrilateralData || 'إدخال بيانات الرباعي ABCD'}</h2>
            <div className="modal-body">
              <div className="input-section">
                <h3>{t.sideLengths || 'أطوال الأضلاع'}:</h3>
                <div className="input-grid">
                  <div className="input-group">
                    <label>{t.sideAB || 'الضلع AB (القاعدة)'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.sideA}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, sideA: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      step="10"
                    />
                  </div>
                  <div className="input-group">
                    <label>{t.sideBC || 'الضلع BC'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.sideB}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, sideB: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      step="10"
                    />
                  </div>
                  <div className="input-group">
                    <label>{t.sideCD || 'الضلع CD'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.sideC}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, sideC: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      step="10"
                    />
                  </div>
                  <div className="input-group">
                    <label>{t.sideDA || 'الضلع DA'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.sideD}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, sideD: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      step="10"
                    />
                  </div>
                </div>
              </div>

              <div className="input-section">
                <h3>{t.diagonals || 'أقطار الرباعي'}:</h3>
                <div className="input-grid">
                  <div className="input-group">
                    <label>{t.diagonal1 || 'القطر الأول (AC)'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.diagonal1}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, diagonal1: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      min="0"
                      step="10"
                    />
                  </div>
                  <div className="input-group">
                    <label>{t.diagonal2 || 'القطر الثاني (BD)'} mm:</label>
                    <input
                      type="number"
                      value={quadrilateralData.diagonal2}
                      onChange={(e) => setQuadrilateralData(prev => ({ ...prev, diagonal2: e.target.value }))}
                      placeholder={t.enterLength || 'أدخل الطول بالـ mm'}
                      min="0"
                      step="10"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-note">
                <p><strong>{t.note || 'ملاحظة'}:</strong> {t.diagonalNote || 'القطر الأول يربط بين النقطتين A و C، والقطر الثاني يربط بين النقطتين B و D.'}</p>
                <p><strong>{t.horizontalBase || 'القاعدة الأفقية'}:</strong> {t.sideABNote || 'الضلع AB سيكون دائماً أفقياً (مستقيماً) كقاعدة للرباعي.'}</p>
                <p><strong>{t.colors || 'الألوان'}:</strong> {t.colorsNote || 'كل ضلع سيُرسم بلون مختلف - AB (أحمر)، BC (أزرق)، CD (أخضر)، DA (برتقالي).'}</p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="modal-btn create-btn" onClick={createQuadrilateral}>
                ✅ {t.create}
              </button>
              <button className="modal-btn cancel-btn" onClick={() => setShowQuadrilateralModal(false)}>
                ❌ {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* منطقة الرسم */}
      <div className="drawing-area">
        <div className="page-bar">
          <div className="page-navigation">
            {/* زر إدارة المشروع */}
            <button 
              className={`page-btn ${showDevicesPage ? 'active' : ''}`}
              onClick={() => setShowDevicesPage(!showDevicesPage)}
              title={'إدارة المشروع'}
            >
              📋 إدارة المشروع
            </button>
            
            {/* أزرار الصفحات الأساسية */}
            <button 
              className={`page-btn ${activePageId === 'floor' && !showDevicesPage ? 'active' : ''}`}
              onClick={() => {
                console.log('Floor button clicked');
                setShowDevicesPage(false);
                switchToPage('floor');
              }}
              title={t.goToFloor || 'انتقل إلى صفحة الأرضية'}
            >
              {t.floor || 'أرضية'}
            </button>
            <button 
              className={`page-btn ${activePageId === 'ceiling' && !showDevicesPage ? 'active' : ''}`}
              onClick={() => {
                console.log('Ceiling button clicked');
                setShowDevicesPage(false);
                switchToPage('ceiling');
              }}
              title={t.goToCeiling || 'انتقل إلى صفحة السقف'}
            >
              {t.ceiling || 'سقف'}
            </button>
            
            {/* أزرار الجدران */}
            {pageOrder.filter(id => id.startsWith('wall')).map(wallId => (
              <div key={wallId} className="wall-page-item">
                <button 
                  className={`page-btn ${activePageId === wallId ? 'active' : ''}`}
                  onClick={() => switchToPage(wallId)}
                  title={`انتقل إلى ${pagesById?.[wallId]?.label || wallId}`}
                >
                  {pagesById?.[wallId]?.label || wallId}
                </button>
                <button 
                  className="remove-wall-btn"
                  onClick={() => removeWallPage(wallId)}
                  title={`حذف ${pagesById?.[wallId]?.label || wallId}`}
                >
                  ×
                </button>
              </div>
            ))}
            
            {/* زر إضافة جدار جديد */}
            <button 
              className={`add-wall-btn`}
              onClick={addWallPage}
              title={t.addNewWall || 'إضافة حائط جديد'}
            >
              + {t.wall || 'حائط'}
            </button>
          </div>
          
          <div className="current-page-info">
            {t.currentPage || 'الصفحة الحالية'}: {pagesById?.[activePageId]?.label || activePageId}
          </div>
        </div>
        <div className="drawing-header">
          <div className="header-controls">
            <button 
              className={`icon-btn ${selectedTool === 'select' ? 'active' : ''}`} 
              onClick={() => setSelectedTool('select')} 
              title={t.select}
            >
              ↖️
            </button>
            <button 
              className={`icon-btn ${selectedTool === 'moveAll' ? 'active' : ''}`} 
              onClick={() => setSelectedTool('moveAll')} 
              title={t.moveAll || 'تحريك الكل'}
            >
              ✋
            </button>
            <button 
              className={`icon-btn ${magnetEnabled ? 'active' : ''}`} 
              onClick={() => setMagnetEnabled(!magnetEnabled)} 
              title={magnetEnabled ? t.magnetEnabled : t.magnet}
            >
              🧲
            </button>
            <button className="icon-btn" onClick={undo} disabled={historyIndex <= 0} title={t.undo}>↶</button>
            <button className="icon-btn" onClick={redo} disabled={historyIndex >= history.length - 1} title={t.redo}>↷</button>
            
            {/* عرض العنصر المحدد */}
            {selectedElement && selectedTool === 'select' && (
              <div className="selected-element-info">
                <span className="element-name" title={`${t.elementName || 'اسم العنصر'}: ${selectedElement.name || selectedElement.label || selectedElement.type}`}>
                  📌 {selectedElement.name || selectedElement.label || (
                    selectedElement.type === 'line' ? t.line :
                    selectedElement.type === 'rectangle' ? t.rectangle :
                    selectedElement.type === 'circle' ? t.circle :
                    selectedElement.type === 'component' ? (t.component || 'مكون') :
                    selectedElement.type === 'baseWall' ? t.baseWall :
                    selectedElement.type === 'text' ? t.text :
                    selectedElement.type === 'dimension' ? t.dimension :
                    selectedElement.type === 'angle' ? t.angle :
                    selectedElement.type === 'customAngle' ? t.customAngle :
                    selectedElement.type
                  )}
                </span>
                <button 
                  className="icon-btn delete-btn" 
                  onClick={() => {
                    deleteElementById(selectedElement.id);
                    setSelectedElement(null);
                    setShowProperties(false);
                  }}
                  title={t.delete || 'حذف العنصر'}
                >
                  🗑️
                </button>
              </div>
            )}
            
            <button className="icon-btn" onClick={() => handleZoom(0.1)} title={t.zoomIn || 'تكبير'}>+</button>
            <button className="icon-btn" onClick={() => handleZoom(-0.1)} title={t.zoomOut || 'تصغير'}>-</button>
            <button className="icon-btn" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title={t.reset || 'إعادة تعيين'}>🎯</button>
            
            {/* أزرار الحفظ والتصدير */}
            <div className="save-export-controls">
              <button 
                className="icon-btn save-btn" 
                onClick={() => {
                  const lastProjectId = localStorage.getItem('cad_last_project_id');
                  if (lastProjectId) {
                    persistActivePageState();
                    alert(t.projectSavedSuccess);
                  } else {
                    alert(t.noActiveProject || 'لا يوجد مشروع نشط للحفظ');
                  }
                }}
                title={t.saveCurrentProject || 'حفظ المشروع الحالي'}
              >
                💾
              </button>
            </div>
            <div className="session-stats">
              <span className="stat-chip" title={t.elementsList}>{elements.length} 📦</span>
              <span className="stat-chip" title={t.zoom}>{(zoom * 100).toFixed(0)}%</span>
              <span className="stat-chip" title={t.tool || 'الأداة'}>{
                selectedTool === 'select' ? t.select : 
                selectedTool === 'moveAll' ? (t.moveAll || 'تحريك الكل') : 
                selectedTool === 'line' ? t.line : 
                selectedTool === 'rectangle' ? t.rectangle : 
                selectedTool === 'circle' ? t.circle : 
                selectedTool === 'dimension' ? t.dimension : 
                selectedTool === 'component' ? (t.component || 'مكون') : 
                selectedTool === 'baseWall' ? t.baseWall :
                selectedTool === 'customAngle' ? t.customAngle :
                selectedTool === 'angle' ? t.angle :
                selectedTool === 'text' ? t.text :
                selectedTool === 'perpendicular' ? (t.perpendicular || 'خطوط متعامدة') : 
                selectedTool
              }</span>
            </div>
          </div>
        </div>
        <div className="canvas-viewport">
          {showDevicesPage ? (
            // Project Management Page (localized)
            <div className="devices-page" style={{ padding: '20px', backgroundColor: '#f5f5f5', height: '100%', overflow: 'auto' }}>
              <h2 style={{ marginBottom: '20px', color: '#333' }}>📋 إدارة المشروع</h2>
              {/* معلومات المشروع */}
              <div style={{ marginBottom: '20px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>📋 {t.projectDetails || 'تفاصيل المشروع'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.projectName || 'Project Name'}:</label>
                    <input
                      type="text"
                      value={projectInfo.projectNumber}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, projectNumber: e.target.value }))}
                      placeholder={t.projectName || 'Project Name'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.client || 'Client'}:</label>
                    <input
                      type="text"
                      value={projectInfo.clientName}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, clientName: e.target.value }))}
                      placeholder={t.client || 'Client'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.address || 'Address'}:</label>
                    <input
                      type="text"
                      value={projectInfo.address}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={t.address || 'Address'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.phone || 'Phone'}:</label>
                    <input
                      type="tel"
                      value={projectInfo.phone}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder={t.phone || 'Phone'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.measuredBy || 'Measured By'}:</label>
                    <input
                      type="text"
                      value={projectInfo.measuredBy}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, measuredBy: e.target.value }))}
                      placeholder={t.measuredBy || 'Measured By'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>{t.branch || 'Branch'}:</label>
                    <input
                      type="text"
                      value={projectInfo.branch}
                      onChange={(e) => setProjectInfo(prev => ({ ...prev, branch: e.target.value }))}
                      placeholder={t.branch || 'Branch'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                </div>
              </div>
              {/* خانة الملاحظة العامة */}
              <div style={{ marginBottom: '20px', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>{t.generalNote || 'ملاحظة عامة'}:</label>
                <textarea
                  value={deviceNote}
                  onChange={(e) => setDeviceNote(e.target.value)}
                  placeholder={t.generalNote || 'ملاحظة عامة'}
                  style={{ width: '100%', minHeight: '80px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              {/* جدول الأجهزة */}
              <div style={{ marginBottom: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#3498db', color: '#fff' }}>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>ID</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.deviceName || 'اسم الجهاز'}</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.model || 'الموديل'}</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.width || 'العرض (W)'}</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.height || 'الارتفاع (H)'}</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.depth || 'العمق (D)'}</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2980b9' }}>{t.notes || 'ملاحظات'}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #2980b9' }}>{t.actions || 'إجراءات'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          {t.noDevices || 'No devices'}
                        </td>
                      </tr>
                    ) : (
                      devices.map((device, index) => (
                        <tr 
                          key={device.id} 
                          style={{ 
                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f9f9f9' : '#fff'}
                        >
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.id}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.name}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.model}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.w}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.h}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.d}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#000' }}>{device.notes}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            <button 
                              onClick={() => selectDeviceForEdit(device)}
                              style={{ padding: '5px 10px', marginRight: '5px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              {t.select || 'تحديد'}
                            </button>
                            <button 
                              onClick={() => deleteDevice(device.id)}
                              style={{ padding: '5px 10px', backgroundColor: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              {t.delete || 'حذف'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* نموذج إضافة/تعديل جهاز */}
              <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '15px', color: '#333' }}>
                  {editingDeviceId ? `✏️ ${t.editDevice || 'تعديل جهاز'}` : `➕ ${t.addDevice || 'إضافة جهاز جديد'}`}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.deviceName || 'اسم الجهاز'} *</label>
                    <input
                      type="text"
                      value={deviceForm.name}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t.deviceName || 'اسم الجهاز'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.model || 'الموديل'} *</label>
                    <input
                      type="text"
                      value={deviceForm.model}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, model: e.target.value }))}
                      placeholder={t.model || 'الموديل'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.width || 'العرض (W)'}</label>
                    <input
                      type="text"
                      value={deviceForm.w}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, w: e.target.value }))}
                      placeholder={t.width || 'العرض (W)'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.height || 'الارتفاع (H)'}</label>
                    <input
                      type="text"
                      value={deviceForm.h}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, h: e.target.value }))}
                      placeholder={t.height || 'الارتفاع (H)'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.depth || 'Depth (D)'}</label>
                    <input
                      type="text"
                      value={deviceForm.d}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, d: e.target.value }))}
                      placeholder={t.depth || 'Depth (D)'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>{t.notes || 'ملاحظات'}</label>
                    <input
                      type="text"
                      value={deviceForm.notes}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder={t.notes || 'ملاحظات'}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  {editingDeviceId ? (
                    <>
                      <button 
                        onClick={updateDevice}
                        style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                      >
                        ✓ {t.update || 'تحديث'}
                      </button>
                      <button 
                        onClick={() => {
                          setEditingDeviceId(null);
                          setDeviceForm({ id: '', name: '', model: '', w: '', h: '', d: '', notes: '' });
                        }}
                        style={{ padding: '10px 20px', backgroundColor: '#95a5a6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                      >
                        {t.cancel || 'إلغاء'}
                      </button>
                    </>
                  ) : (
                    <button 
                      onClick={addDevice}
                      style={{ padding: '10px 20px', backgroundColor: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                    >
                      ➕ {t.addDevice || t.add || 'Add Device'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // منطقة الرسم الأصلية
            <div
              className="canvas-content"
              style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative' }}
            >
              <svg
              ref={svgRef}
              className={`drawing-canvas ${selectedTool === 'select' ? 'select-mode' : ''} ${selectedTool !== 'select' ? 'drawing-mode' : ''} ${isPanning ? 'panning' : ''}`}
              width="5000"
              height="5000"
              viewBox="0 0 5000 5000"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '5000px',
                height: '5000px',
                minWidth: '100vw',
                minHeight: '100vh',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                background: '#fff'
              }}
            >
          {/* خلفية */}
          <rect width="1500" height="1500" fill="#ffffff" />
                    <rect width="5000" height="5000" fill="#ffffff" />
          
          {/* الشبكة */}
          <g className="grid" opacity={snapToGrid ? "1" : "0.3"}>
            {renderGrid()}
          </g>
          
          {/* المحاور */}
          <g className="axes">
            <line x1="0" y1="0" x2="1500" y2="0" stroke="#ff0000" strokeWidth="1" opacity="0.7" />
            <line x1="0" y1="0" x2="0" y2="1500" stroke="#00ff00" strokeWidth="1" opacity="0.7" />
            {/* تسميات المحاور */}
            <text x="1180" y="15" fill="#ff0000" fontSize="12" fontFamily="Arial">X</text>
            <text x="10" y="20" fill="#00ff00" fontSize="12" fontFamily="Arial">Y</text>
          </g>
          
          {/* تحديد منطقة الحائط الأساسي عند استخدام أداة component */}
          {baseWall && selectedTool === 'component' && (
            <rect
              x={Math.min(baseWall.x, baseWall.x + baseWall.width)}
              y={Math.min(baseWall.y, baseWall.y + baseWall.height)}
              width={Math.abs(baseWall.width)}
              height={Math.abs(baseWall.height)}
              fill="rgba(59, 130, 246, 0.1)"
              stroke="#3b82f6"
              strokeWidth="2"
              strokeDasharray="5,5"
              pointerEvents="none"
            />
          )}
          
          {/* العناصر المرسومة مقسمة حسب الطبقات */}
          {Object.keys(layers).map(layerKey => (
            <g key={layerKey} className={`layer-${layerKey}`}>
              {elements
                .filter(element => element.layer === layerKey && layers[layerKey].visible && element.visible !== false)
                .map((element, idx) => {
                  return renderElement(element, selectedElement?.id === element.id, layers[layerKey], null);
                })}
            </g>
          ))}
          
          {/* خطوط المسافة المتقطعة بين العناصر والحائط الأساسي */}
          {baseWall && (
            <g className="distance-lines">
              {elements
                .filter(el => el.type === 'component' && el.type !== 'baseWall' && el.visible !== false)
                .map(element => {
                  const distanceLines = calculateDistanceLines(element);
                  return distanceLines.map((line, idx) => (
                    <g key={`${element.id}-dist-${idx}`}>
                      <line
                        x1={line.x1}
                        y1={line.y1}
                        x2={line.x2}
                        y2={line.y2}
                        stroke="#9b59b6"
                        strokeWidth="1"
                        strokeDasharray="5,5"
                        opacity="0.6"
                      />
                      <text
                        x={line.midX}
                        y={line.midY}
                        fill="#9b59b6"
                        fontSize="10"
                        textAnchor="middle"
                        fontFamily="Arial"
                      >
                        {line.distance} mm
                      </text>
                    </g>
                  ));
                })}
            </g>
          )}
          
          {/* العنصر الحالي أثناء الرسم */}
          {currentElement && (
            <g className="current-element">
              {renderElement(currentElement, false, null)}
            </g>
          )}
          
          {/* مؤشر نقطة الالتصاق (Magnet Snap Point) */}
          {snapPoint && magnetEnabled && (
            <g className="snap-indicator">
              <circle
                cx={snapPoint.x}
                cy={snapPoint.y}
                r={8}
                fill="none"
                stroke="#ff0000"
                strokeWidth={2}
                opacity={0.8}
              />
              <circle
                cx={snapPoint.x}
                cy={snapPoint.y}
                r={3}
                fill="#ff0000"
                opacity={0.8}
              />
            </g>
          )}
          
          {/* مؤشرات التحديد للعنصر المحدد */}
          {selectedElement && (
            <g className="selection-indicators">
              {selectedElement.type === 'line' && (
                <>
                  <circle cx={selectedElement.x1} cy={selectedElement.y1} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x2} cy={selectedElement.y2} r="4" fill="#ff0000" />
                </>
              )}
              {selectedElement.type === 'rectangle' && (
                <>
                  <circle cx={selectedElement.x} cy={selectedElement.y} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y + selectedElement.height} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x} cy={selectedElement.y + selectedElement.height} r="4" fill="#ff0000" />
                </>
              )}
              {selectedElement.type === 'component' && (
                <>
                  <circle cx={selectedElement.x} cy={selectedElement.y} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y + selectedElement.height} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.x} cy={selectedElement.y + selectedElement.height} r="4" fill="#ff0000" />
                </>
              )}
              {selectedElement.type === 'baseWall' && (
                <>
                  <circle cx={selectedElement.x} cy={selectedElement.y} r="5" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y} r="5" fill="#ff0000" />
                  <circle cx={selectedElement.x + selectedElement.width} cy={selectedElement.y + selectedElement.height} r="5" fill="#ff0000" />
                  <circle cx={selectedElement.x} cy={selectedElement.y + selectedElement.height} r="5" fill="#ff0000" />
                </>
              )}
              {selectedElement.type === 'circle' && (
                <>
                  <circle cx={selectedElement.cx} cy={selectedElement.cy} r="4" fill="#ff0000" />
                  <circle cx={selectedElement.cx + selectedElement.r} cy={selectedElement.cy} r="3" fill="#ff0000" />
                </>
              )}
              {selectedElement.type === 'quadrilateral' && (
                <>
                  {selectedElement.points.map((point, index) => (
                    <circle key={index} cx={point.x} cy={point.y} r="4" fill="#ff0000" opacity="0.8" />
                  ))}
                  {/* نقطة تقاطع الأقطار */}
                  <circle 
                    cx={(selectedElement.points[0].x + selectedElement.points[2].x) / 2} 
                    cy={(selectedElement.points[0].y + selectedElement.points[2].y) / 2} 
                    r="6" 
                    fill="#ff0000" 
                    opacity="0.9"
                    stroke="#ffffff"
                    strokeWidth="1"
                  />
                </>
              )}
            </g>
          )}
          
          {/* مؤشر الالتصاق بالشبكة */}
          {snapToGrid && (
            <circle
              cx={Math.round(mousePosition.x / gridSize) * gridSize}
              cy={Math.round(mousePosition.y / gridSize) * gridSize}
              r="3"
              fill="#ff0000"
              opacity="0.5"
            />
          )}
        </svg>
            </div>
          )}
        </div>
        
        {/* تحذير الحدود */}
        {showBoundaryWarning && (
          <div className="boundary-warning">
            ⚠️ لا يمكن نقل العنصر خارج الحائط الأساسي
          </div>
        )}
        
        {/* شريط الحالة */}
        <div className="status-bar">
          <div className="status-left">
            <span>الإحداثيات: X: {mousePosition.x}, Y: {mousePosition.y}</span>
            {snapToGrid && <span className="snap-indicator">🔒 ملتصق بالشبكة</span>}
            {magnetEnabled && <span className="magnet-indicator" style={{ color: '#4CAF50' }}>🧲 المغناطيس مفعل</span>}
            {selectedElement && (
              <span className="selected-info">
                {selectedElement.type === 'line' ? 'خط' :
                  selectedElement.type === 'rectangle' ? 'مستطيل' :
                  selectedElement.type === 'circle' ? 'دائرة' :
                  selectedElement.type === 'component' ? 'مكون' : selectedElement.type} (ID: {selectedElement.id})
              </span>
            )}
          </div>
          <div className="status-right">
            {selectedElement ? (
              <>
                <span>طبقة العنصر: {singleLabel(selectedElement.layer)}</span>
                <span style={{ color: layers[selectedElement.layer].color }}>●</span>
              </>
            ) : (
              <>
                <span>الطبقة النشطة: {singleLabel('baseLayer')}</span>
                <span style={{ color: layers['layer1'].color }}>●</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* نافذة إدخال قياس الزاوية */}
      {showAngleModal && (
        <div className="modal-overlay" onClick={cancelAngle}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>إدخال قياس الزاوية</h3>
              <button className="modal-close" onClick={cancelAngle}>×</button>
            </div>
            <div className="modal-body">
              <p>أدخل قياس الزاوية بالدرجات (من 0° إلى 360°):</p>
              <input
                type="number"
                value={angleValue}
                onChange={(e) => setAngleValue(e.target.value)}
                min="0"
                max="360"
                step="1"
                className="angle-input"
                placeholder="مثال: 90"
                onKeyPress={(e) => e.key === 'Enter' && confirmAngle()}
                autoFocus
              />
              <div className="angle-preview">
                <span>الزاوية: {angleValue || 0}°</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={cancelAngle}>
                إلغاء
              </button>
              <button className="btn-primary" onClick={confirmAngle}>
                تأكيد
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة إدخال النص */}
      {showTextModal && (
        <div className="modal-overlay" onClick={cancelText}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>إدخال نص</h3>
              <button className="modal-close" onClick={cancelText}>×</button>
            </div>
            <div className="modal-body">
              <label>النص:</label>
              <input
                type="text"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                className="angle-input"
                placeholder="أدخل النص المطلوب"
                onKeyPress={(e) => e.key === 'Enter' && confirmText()}
                autoFocus
              />
              <label>الحجم:</label>
              <select
                value={textSizeValue}
                onChange={(e) => setTextSizeValue(e.target.value)}
                className="layer-select"
              >
                <option value="12">12</option>
                <option value="14">14</option>
                <option value="16">16</option>
                <option value="18">18</option>
                <option value="20">20</option>
                <option value="22">22</option>
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={cancelText}>إلغاء</button>
              <button className="btn-primary" onClick={confirmText}>تأكيد</button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة إدخال بيانات المكون */}
      {showComponentModal && (
        <div className="modal-overlay" onClick={cancelComponentInput}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 {t.componentDataEntry || 'إدخال بيانات'} {t[selectedComponentLabel] || selectedComponentLabel}</h3>
              <button className="modal-close" onClick={cancelComponentInput}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label>{t.widthMM || 'العرض (mm)'}:</label>
                <input
                  type="number"
                  value={componentInputData.width}
                  onChange={(e) => setComponentInputData(prev => ({ ...prev, width: e.target.value }))}
                  className="angle-input"
                  placeholder={t.enterWidth || 'أدخل العرض بالـ mm'}
                  min="0"
                  step="10"
                />
              </div>
              <div className="input-group">
                <label>{t.heightMM || 'الارتفاع (mm)'}:</label>
                <input
                  type="number"
                  value={componentInputData.height}
                  onChange={(e) => setComponentInputData(prev => ({ ...prev, height: e.target.value }))}
                  className="angle-input"
                  placeholder={t.enterHeight || 'أدخل الارتفاع بالـ mm'}
                  min="0"
                  step="10"
                />
              </div>
              <div className="input-group">
                <label>{t.distanceFromBottom || 'المسافة من الأسفل (mm)'}:</label>
                <input
                  type="number"
                  value={componentInputData.distanceFromBottom}
                  onChange={(e) => setComponentInputData(prev => ({ ...prev, distanceFromBottom: e.target.value }))}
                  className="angle-input"
                  placeholder={t.enterHeight || 'المسافة من الأسفل بالـ mm'}
                  min="0"
                  step="10"
                />
              </div>
              <div className="input-group">
                <label>{t.distanceFromLeft || 'المسافة من الضلع الأيسر (mm)'}:</label>
                <input
                  type="number"
                  value={componentInputData.distanceFromLeft}
                  onChange={(e) => setComponentInputData(prev => ({ ...prev, distanceFromLeft: e.target.value }))}
                  className="angle-input"
                  placeholder={t.enterWidth || 'المسافة من اليسار بالـ mm'}
                  min="0"
                  step="10"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={cancelComponentInput}>{t.cancel}</button>
              <button className="btn-primary" onClick={confirmComponentInput}>{t.add}</button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة إدخال بيانات الحائط المنفرد */}
      {showCustomAngleModal && (
        <div className="modal-overlay" onClick={() => setShowCustomAngleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', padding: '20px' }}>
            <div className="modal-header">
              <h3 style={{ fontSize: '1.2rem', margin: 0 }}>🧱 {t.customAngle}</h3>
              <button className="modal-close" onClick={() => setShowCustomAngleModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ gap: '12px' }}>
              <div className="input-group">
                <label style={{ fontSize: '0.85rem' }}>{t.angleMeasurement || 'قياس الزاوية'} (°):</label>
                <input
                  type="number"
                  value={customAngleData.angle}
                  onChange={(e) => setCustomAngleData(prev => ({ ...prev, angle: e.target.value }))}
                  className="angle-input"
                  placeholder="90"
                  min="0"
                  max="360"
                  step="1"
                  style={{ padding: '6px' }}
                />
              </div>
              
              <div className="input-group">
                <label style={{ fontSize: '0.85rem' }}>{t.angleDirection || 'اتجاه الزاوية'}:</label>
                <select
                  value={selectedAngleDirection}
                  onChange={(e) => setSelectedAngleDirection(e.target.value)}
                  className="angle-input"
                  style={{ padding: '6px' }}
                >
                  <option value="horizontal_right_up">{t.horizontalRightUp || 'أفقي يمين - أعلى ↗'}</option>
                  <option value="horizontal_right_down">{t.horizontalRightDown || 'أفقي يمين - أسفل ↘'}</option>
                  <option value="horizontal_left_up">{t.horizontalLeftUp || 'أفقي يسار - أعلى ↖'}</option>
                  <option value="horizontal_left_down">{t.horizontalLeftDown || 'أفقي يسار - أسفل ↙'}</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#27ae60', display: 'block', marginBottom: '5px' }}>{t.firstLine || 'الخط الأول'}:</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={customAngleData.name1}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, name1: e.target.value }))}
                    className="angle-input"
                    placeholder={t.name || 'الاسم (AB)'}
                    style={{ padding: '6px', flex: '1' }}
                  />
                  <input
                    type="number"
                    value={customAngleData.length1}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, length1: e.target.value }))}
                    className="angle-input"
                    placeholder={t.enterLength || 'الطول mm'}
                    min="10"
                    step="10"
                    style={{ padding: '6px', flex: '1' }}
                  />
                  <input
                    type="number"
                    value={customAngleData.lineWidth1}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, lineWidth1: e.target.value }))}
                    className="angle-input"
                    placeholder={t.thickness || 'السُمك'}
                    min="1"
                    step="1"
                    style={{ padding: '6px', flex: '1' }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#3498db', display: 'block', marginBottom: '5px' }}>{t.secondLine || 'الخط الثاني'}:</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={customAngleData.name2}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, name2: e.target.value }))}
                    className="angle-input"
                    placeholder={t.name || 'الاسم (AC)'}
                    style={{ padding: '6px', flex: '1' }}
                  />
                  <input
                    type="number"
                    value={customAngleData.length2}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, length2: e.target.value }))}
                    className="angle-input"
                    placeholder={t.enterLength || 'الطول mm'}
                    min="10"
                    step="10"
                    style={{ padding: '6px', flex: '1' }}
                  />
                  <input
                    type="number"
                    value={customAngleData.lineWidth2}
                    onChange={(e) => setCustomAngleData(prev => ({ ...prev, lineWidth2: e.target.value }))}
                    className="angle-input"
                    placeholder={t.thickness || 'السُمك'}
                    min="1"
                    step="1"
                    style={{ padding: '6px', flex: '1' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ paddingTop: '12px', marginTop: '12px' }}>
              <button className="btn-secondary" onClick={() => setShowCustomAngleModal(false)} style={{ padding: '8px 16px', fontSize: '0.9rem' }}>{t.cancel}</button>
              <button className="btn-primary" onClick={createCustomAngle} style={{ padding: '8px 16px', fontSize: '0.9rem' }}>{t.create}</button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة اختيار اتجاه الباب الأرضي */}
      {showFloorDoorDirectionModal && (
        <div className="modal-overlay" onClick={() => setShowFloorDoorDirectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', padding: '30px' }}>
            <div className="modal-header">
              <h3 style={{ fontSize: '1.4rem', margin: 0, textAlign: 'center' }}>🚪 اختر نموذج الباب الأرضي</h3>
              <button className="modal-close" onClick={() => setShowFloorDoorDirectionModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '18px' }}>
                {[...FLOOR_DOOR_DIRECTIONS, ...FLOOR_DOOR_DIRECTIONS_FLIPPED].map(dir => (
                  <div
                    key={dir.key}
                    style={{
                      border: selectedFloorDoorDirection === dir.key ? '3px solid #3b82f6' : '2px solid #374151',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      backgroundColor: selectedFloorDoorDirection === dir.key ? '#eff6ff' : '#f9fafb'
                    }}
                    onClick={() => selectFloorDoorDirection(dir.key)}
                  >
                    {/* تم حذف أيقونة السهم والنص */}
                    <svg width="60" height="60" viewBox="0 0 60 60" style={{ margin: '10px 0' }}>
                      {/* رسم الخطوط حسب النموذج */}
                      {(() => {
                        let flip = false;
                        let baseDir = dir.key;
                        if (dir.key.endsWith('-flip')) {
                          flip = true;
                          baseDir = dir.key.replace('-flip', '');
                        }
                        let line1, line2, arc;
                        if (baseDir === 'bottom-left') {
                          line1 = <line x1="10" y1="10" x2="10" y2="50" stroke={flip ? '#ff0000' : '#fff'} strokeWidth="4"/>;
                          line2 = <line x1="10" y1="50" x2="50" y2="50" stroke={flip ? '#fff' : '#ff0000'} strokeWidth="3"/>;
                          arc = <path d="M 10 10 A 30 30 0 0 1 50 50" stroke="#374151" strokeWidth="1.5" fill="none" strokeDasharray="3,2"/>;
                        } else if (baseDir === 'bottom-right') {
                          line1 = <line x1="50" y1="10" x2="50" y2="50" stroke={flip ? '#ff0000' : '#fff'} strokeWidth="4"/>;
                          line2 = <line x1="10" y1="50" x2="50" y2="50" stroke={flip ? '#fff' : '#ff0000'} strokeWidth="3"/>;
                          arc = <path d="M 50 10 A 30 30 0 0 0 10 50" stroke="#374151" strokeWidth="1.5" fill="none" strokeDasharray="3,2"/>;
                        } else if (baseDir === 'top-left') {
                          line1 = <line x1="10" y1="10" x2="10" y2="50" stroke={flip ? '#ff0000' : '#fff'} strokeWidth="4"/>;
                          line2 = <line x1="10" y1="10" x2="50" y2="10" stroke={flip ? '#fff' : '#ff0000'} strokeWidth="3"/>;
                          arc = <path d="M 10 50 A 30 30 0 0 0 50 10" stroke="#374151" strokeWidth="1.5" fill="none" strokeDasharray="3,2"/>;
                        } else if (baseDir === 'top-right') {
                          line1 = <line x1="50" y1="10" x2="50" y2="50" stroke={flip ? '#ff0000' : '#fff'} strokeWidth="4"/>;
                          line2 = <line x1="10" y1="10" x2="50" y2="10" stroke={flip ? '#fff' : '#ff0000'} strokeWidth="3"/>;
                          arc = <path d="M 50 50 A 30 30 0 0 1 10 10" stroke="#374151" strokeWidth="1.5" fill="none" strokeDasharray="3,2"/>;
                        }
                        return <>{line1}{line2}{arc}</>;
                      })()}
                    </svg>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer" style={{ paddingTop: '20px', textAlign: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => setShowFloorDoorDirectionModal(false)}
                style={{ padding: '10px 20px', marginRight: '10px' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SketchPage;
